import { useState, useEffect, useCallback, Fragment, useRef, useMemo, memo } from 'react';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api, { 
  getGarmentsPaginated, 
  getGarment,
  getGarmentDispatchDateOptions,
  getGarmentPlanStatus,
  updateGarment, 
  updateGarmentStatus, 
  deleteGarment,
  getCurrentUser,
  getTailors,
  createTailor,
  updateTailor,
  deleteTailor,
  bulkUpdateTailors,
  getGarmentTaskConfigs,
  createGarmentTaskConfig,
  updateGarmentTaskConfig,
  deleteGarmentTaskConfig,
  seedGarmentTaskConfigs,
  getProductionOverview,
  getTailorWorkloads,
  getPatternWorkloads,
  autoAssignProduction,
  getProductionTasks,
  updateProductionTask,
  bulkDeleteProductionTasks,
  moveProductionTask,
  planPreview,
  applyAssignments,
  getProductionAdHocOptions,
  recommendProductionAdHocTask,
  createProductionAdHocTask,
  getProducts
} from '@/lib/api';
import { generateOrderSheetForGarment } from '@/lib/orderSheetPdf';
import GarmentImagesModal from '@/components/garments/GarmentImagesModal';
import PatternSelectorModal from '@/components/garments/PatternSelectorModal';
import { 
  Search, Shirt, Filter, Image as ImageIcon, FileText, 
  MoreVertical, Trash2, Calendar as CalendarIcon, X, StickyNote, Loader2, Download,
  ChevronDown, ChevronRight, ChevronUp, Eye, Copy, Info, Edit,
  Users, ListChecks, ClipboardList, Gauge, Plus, RefreshCcw, AlertTriangle, CheckCircle2, ArrowUpDown,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { format } from 'date-fns';
import { useDebouncedCallback } from '@/hooks/useDebounce';
import { formatCustomerNo, extractGarmentSuffix, formatGarmentTypeLabel } from '@/lib/displayUtils';
import { sortCustomizations } from '@/lib/customizationUtils';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { formatDateWord } from '@/lib/dateFormat';
import DateField from '@/components/common/DateField';

const API_URL = getApiBaseUrl();

// Status configurations
const garmentStatuses = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-600/80 text-white border-slate-600' },
  { value: 'not_started', label: 'Not Started', color: 'bg-amber-600/80 text-white border-amber-600' },
  { value: 'awaiting_fabric', label: 'Awaiting Fabric', color: 'bg-orange-500/80 text-white border-orange-500' },
  { value: 'under_cutting', label: 'Under Cutting', color: 'bg-blue-600/80 text-white border-blue-600' },
  { value: 'under_sewing', label: 'Under Sewing', color: 'bg-purple-600/80 text-white border-purple-600' },
  { value: 'finishing', label: 'Finishing', color: 'bg-cyan-600/80 text-white border-cyan-600' },
  { value: 'quality_check', label: 'Quality Check', color: 'bg-indigo-600/80 text-white border-indigo-600' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-600/80 text-white border-emerald-600' },
];

const patternStatuses = [
  { value: 'not_ready', label: 'Not Ready', color: 'bg-red-900/80 text-white border-red-900' },
  { value: 'for_approval', label: 'For Approval', color: 'bg-amber-600/80 text-white border-amber-600' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-600/80 text-white border-emerald-600' },
  { value: 'printed', label: 'Printed', color: 'bg-blue-700/80 text-white border-blue-700' },
  { value: 'ready', label: 'Ready', color: 'bg-teal-600/80 text-white border-teal-600' },
];

const priorities = [
  { value: 'low', label: 'Low', color: 'bg-blue-600/80 text-white border-blue-600' },
  { value: 'normal', label: 'Normal', color: 'bg-slate-600/80 text-white border-slate-600' },
  { value: 'high', label: 'High', color: 'bg-orange-700/80 text-white border-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-900/80 text-white border-red-900' },
];

const GARMENTS_VIRTUAL_ROW_HEIGHT = 56;
const GARMENTS_VIRTUAL_OVERSCAN = 12;

const GarmentsSearchInput = memo(function GarmentsSearchInput({ defaultValue, onDebouncedChange }) {
  const [localValue, setLocalValue] = useState(defaultValue || '');

  useEffect(() => {
    setLocalValue(defaultValue || '');
  }, [defaultValue]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const trimmed = (localValue || '').trim();
      if (trimmed.length === 0) {
        onDebouncedChange('');
        return;
      }
      if (trimmed.length >= 3) {
        onDebouncedChange(trimmed);
        return;
      }
      onDebouncedChange('');
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [localValue, onDebouncedChange]);

  return (
    <div className="relative w-full md:w-[360px] md:min-w-[320px]" data-testid="garments-search-input-container">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#718096]" />
      <Input
        placeholder="Search garments, customers, orders, pattern no... (min 3 chars)"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className="pl-10 bg-white border-[#e2e8f0] h-9"
        data-testid="garments-search-input"
      />
    </div>
  );
});

const GarmentExpandedRowContent = memo(function GarmentExpandedRowContent({
  garment,
  onViewCustomizations,
  onUpdateGarmentField,
  getFabricStatusConfig,
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Order Information</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Client Delivery:</span>
            <span className="font-medium text-slate-900">
              {garment.client_final_date ? formatDateWord(garment.client_final_date, 'Not set') : 'Not set'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Internal Delivery:</span>
            <span className="font-medium text-slate-900">
              {garment.internal_delivery_date ? formatDateWord(garment.internal_delivery_date, 'Not set') : 'Not set'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Test Fitting:</span>
            <span className="font-medium text-slate-900">
              {garment.test_fit_date ? formatDateWord(garment.test_fit_date, 'Not set') : 'Not set'}
            </span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Fabric Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Main Fabric:</span>
            <span className="font-medium text-slate-900">{garment.main_fabric_sku || 'Not assigned'}</span>
            {garment.main_fabric_status && (
              <Badge variant="outline" className={`text-xs ${getFabricStatusConfig(garment.main_fabric_status).color}`}>
                {getFabricStatusConfig(garment.main_fabric_status).label}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Lining:</span>
            <span className="font-medium text-slate-900">{garment.lining_fabric_sku || 'Not assigned'}</span>
            {garment.lining_fabric_status && (
              <Badge variant="outline" className={`text-xs ${getFabricStatusConfig(garment.lining_fabric_status).color}`}>
                {getFabricStatusConfig(garment.lining_fabric_status).label}
              </Badge>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewCustomizations(garment);
            }}
            className="h-8 text-xs"
          >
            <Eye className="w-3.5 h-3.5 mr-2" />
            View Customizations
          </Button>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Notes</h4>
        <Textarea
          value={garment.notes || ''}
          onChange={(e) => onUpdateGarmentField(garment.garment_id, 'notes', e.target.value)}
          placeholder="Add notes about this garment..."
          className="min-h-[120px] text-sm bg-white border-slate-200 resize-none"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
});

const priorityRank = {
  urgent: 4,
  high: 3,
  normal: 2,
  low: 1,
};

const getPriorityRank = (priority) => priorityRank[(priority || '').toLowerCase()] || 0;

// Fabric status configurations (matching FabricProcurementPage colors)
const fabricStatuses = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-600/80 text-white border-slate-600' },
  { value: 'not_ordered', label: 'Not Ordered', color: 'bg-red-900/80 text-white border-red-900' },
  { value: 'checking_supplier', label: 'Checking with Supplier', color: 'bg-orange-500/80 text-white border-orange-500' },
  { value: 'order_confirmed', label: 'Order Confirmed', color: 'bg-blue-600/80 text-white border-blue-600' },
  { value: 'in_transit', label: 'In Transit', color: 'bg-purple-600/80 text-white border-purple-600' },
  { value: 'received_sg', label: 'Received in SG', color: 'bg-green-700/80 text-white border-green-700' },
  { value: 'received_gobi', label: 'Received in Gobi', color: 'bg-green-800/80 text-white border-green-800' },
];

const orderSheetStatuses = [
  { value: 'not_ready', label: 'Not Ready' },
  { value: 'approved', label: 'Approved' },
  { value: 'printed', label: 'Printed' },
];

// Sample pattern makers - in production this would come from API
const patternMakers = [
  { value: '__none__', label: 'Not assigned' },
  { value: 'john_doe', label: 'John Doe' },
  { value: 'jane_smith', label: 'Jane Smith' },
  { value: 'mike_wilson', label: 'Mike Wilson' },
];

const baseTailorCapabilities = [
  { value: 'cutting', label: 'Cutting' },
  { value: 'sewing', label: 'Sewing' },
  { value: 'inner_sewing', label: 'Inner + Sleeve Sewing' },
  { value: 'outer_sewing', label: 'Outer Sewing' },
  { value: 'pattern', label: 'Pattern' },
];

const taskCategoryOptions = ['Cut', 'Sewing', 'Handwork', 'Others'];

const predefinedTaskLibrary = [
  { key: 'cut', label: 'Cut', category: 'Cut', task_type: 'cutting', required_capability: 'cutting' },
  { key: 'inner_sleeve_sewing', label: 'Inner + Sleeve Sewing', category: 'Sewing', task_type: 'inner_sewing', required_capability: 'inner_sewing' },
  { key: 'outer_sewing', label: 'Outer Sewing', category: 'Sewing', task_type: 'outer_sewing', required_capability: 'outer_sewing' },
  { key: 'inner_outer_sewing', label: 'Inner + Outer Sewing', category: 'Sewing', task_type: 'sewing', required_capability: 'sewing' },
  { key: 'alteration', label: 'Alteration', category: 'Sewing', task_type: 'sewing', required_capability: 'alteration' },
  { key: 'milanese_buttonhole', label: 'Milanese Buttonhole', category: 'Handwork', task_type: 'sewing', required_capability: 'milanese_buttonhole' },
  { key: 'buttonhole', label: 'Buttonhole', category: 'Others', task_type: 'sewing', required_capability: 'buttonhole' },
  { key: 'buttonstitch', label: 'Buttonstitch', category: 'Others', task_type: 'sewing', required_capability: 'buttonstitch' },
];

const tailorTeamOptions = [
  { value: 'Jacket/Overcoat/Waistcoat', label: 'Jacket/Overcoat/Waistcoat' },
  { value: 'Trouser', label: 'Trouser' },
  { value: 'Shirt', label: 'Shirt' },
  { value: 'Trouser/Shirt', label: 'Trouser/Shirt' },
  { value: 'Pattern', label: 'Pattern' },
  { value: 'Other', label: 'Other' },
];

const taskTypeOptions = [
  { value: 'pattern', label: 'Pattern' },
  { value: 'cutting', label: 'Cutting' },
  { value: 'inner_sewing', label: 'Inner + Sleeve Sewing' },
  { value: 'outer_sewing', label: 'Outer Sewing' },
  { value: 'sewing', label: 'Sewing' },
  { value: 'ordering', label: 'Ordering' },
];

const GarmentsPage = () => {
  const [garments, setGarments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const garmentsFetchAbortRef = useRef(null);
  const garmentsFetchSeqRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null); // For collapsible rows (accordion)
  const [garmentDetailsById, setGarmentDetailsById] = useState({});
  
  // Read URL params for KPI click-through filtering
  const urlParams = new URLSearchParams(window.location.search);
  const initialStore = urlParams.get('store') || 'all';
  const initialStatus = urlParams.get('status') || 'all';
  const initialPatternStatus = urlParams.get('pattern_status') || 'all';
  const initialPriority = urlParams.get('priority') || 'all';
  const initialDispatchFilter = urlParams.get('no_dispatch') === '1' ? 'no_dispatch' : null;
  const initialNoPattern = urlParams.get('no_pattern') === '1' ? true : false;
  const initialDispatchDate = urlParams.get('planned_dispatch_date') || null;
  
  // Stores list - fetched dynamically based on user access
  const [stores, setStores] = useState([]);
  
  // Image manager state
  const [imageManagerOpen, setImageManagerOpen] = useState(false);
  const [selectedGarmentForImages, setSelectedGarmentForImages] = useState(null);
  
  // Customizations dialog state
  const [customizationsOpen, setCustomizationsOpen] = useState(false);
  const [selectedGarmentForCustomizations, setSelectedGarmentForCustomizations] = useState(null);
  const [deliveryDateEditorOpen, setDeliveryDateEditorOpen] = useState(false);
  const [deliveryDateGarment, setDeliveryDateGarment] = useState(null);
  const [deliveryDateForm, setDeliveryDateForm] = useState({
    internal_delivery_date: '',
    client_final_date: '',
  });
  const [deliveryDateSaving, setDeliveryDateSaving] = useState(false);
  
  // Pattern selector modal state
  const [patternSelectorOpen, setPatternSelectorOpen] = useState(false);
  const [selectedGarmentForPattern, setSelectedGarmentForPattern] = useState(null);
  
  // Order sheet warning dialog state
  const [noPatternWarningOpen, setNoPatternWarningOpen] = useState(false);
  const [garmentPendingOrderSheet, setGarmentPendingOrderSheet] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 60; // Balanced for smoother UI while keeping infinite scroll efficient
  const garmentsTableScrollRef = useRef(null);
  const [garmentsTableScrollTop, setGarmentsTableScrollTop] = useState(0);
  const [garmentsTableViewportHeight, setGarmentsTableViewportHeight] = useState(640);
  
  const debouncedSearch = searchTerm;

  const handleSearchTermChange = useCallback((nextValue) => {
    setSearchTerm(nextValue);
  }, []);

  const handleGarmentsTableScroll = useCallback((event) => {
    setGarmentsTableScrollTop(event.currentTarget.scrollTop || 0);
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      const nextHeight = garmentsTableScrollRef.current?.clientHeight;
      if (nextHeight && Number.isFinite(nextHeight)) {
        setGarmentsTableViewportHeight(nextHeight);
      }
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);
  
  // Filters - initialize from URL params if present
  const [filters, setFilters] = useState({
    status: initialStatus,
    priority: initialPriority,
    garmentType: 'all',
    patternStatus: initialPatternStatus,
    patternMaker: 'all',
    store: initialStore,
    dispatchDate: initialDispatchDate,
  });
  const [noDispatchFilter, setNoDispatchFilter] = useState(initialDispatchFilter);
  const [noPatternFilter, setNoPatternFilter] = useState(initialNoPattern);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkValues, setBulkValues] = useState({ priority: '', garment_status: '', pattern_maker: '', planned_dispatch_date: null });

  const [activeTab, setActiveTab] = useState('garments');
  const [currentUser, setCurrentUser] = useState(null);
  const [hasProductionAccess, setHasProductionAccess] = useState(false);
  const [productionOverview, setProductionOverview] = useState({ unassigned_tasks: 0 });

  const [tailors, setTailors] = useState([]);
  const [tailorsLoading, setTailorsLoading] = useState(false);
  const [tailorDialogOpen, setTailorDialogOpen] = useState(false);
  const [editingTailor, setEditingTailor] = useState(null);
  const [tailorSortBy, setTailorSortBy] = useState('employee_id');
  const [tailorSortDir, setTailorSortDir] = useState('asc');
  const [tailorFilterTeam, setTailorFilterTeam] = useState('all');
  const [tailorFilterStatus, setTailorFilterStatus] = useState('active');
  const [tailorForm, setTailorForm] = useState({
    name: '',
    employee_id: '',
    team: '',
    primary_task: '',
    capabilities: [],
    is_active: true,
  });

  // Bulk tailor selection & edit
  const [selectedTailorIds, setSelectedTailorIds] = useState(new Set());
  const [tailorBulkEditOpen, setTailorBulkEditOpen] = useState(false);
  const [tailorBulkField, setTailorBulkField] = useState('primary_garment_types');
  const [tailorBulkAction, setTailorBulkAction] = useState('add');
  const [tailorBulkValues, setTailorBulkValues] = useState([]);
  const [tailorBulkBoolValue, setTailorBulkBoolValue] = useState(true);
  const [tailorBulkSaving, setTailorBulkSaving] = useState(false);

  // Assignment planning modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [planPreviewData, setPlanPreviewData] = useState(null);
  const [planPreviewLoading, setPlanPreviewLoading] = useState(false);
  // draftAssignments: { `${garment_id}__${task_key}` : tailor_id }
  const [draftAssignments, setDraftAssignments] = useState({});
  const [applyingAssignments, setApplyingAssignments] = useState(false);
  const [dispatchDateOptions, setDispatchDateOptions] = useState([]);
  const [dispatchDateOptionsLoading, setDispatchDateOptionsLoading] = useState(false);
  const [garmentPlanStatusByGarment, setGarmentPlanStatusByGarment] = useState({});
  // Tailor Workload sub-tab
  const [workloadSubTab, setWorkloadSubTab] = useState('tailors');
  const [calendarTeamFilter, setCalendarTeamFilter] = useState('all');
  const [calendarTailorFilter, setCalendarTailorFilter] = useState('all');

  const [taskConfigs, setTaskConfigs] = useState([]);
  const [taskConfigDialogOpen, setTaskConfigDialogOpen] = useState(false);
  const [editingTaskConfig, setEditingTaskConfig] = useState(null);
  const [taskConfigForm, setTaskConfigForm] = useState({
    garment_type: '',
    production_required: true,
    tasks: [],
  });
  const [products, setProducts] = useState([]);
  const garmentTypeOptions = Array.from(
    new Set((products || [])
      .map(p => p.default_garment_type || p.garment_type || p.product_name)
      .filter(Boolean))
  ).sort();

  // Derived: filtered + sorted tailors list for display
  const displayedTailors = (() => {
    let list = [...tailors];
    if (tailorFilterTeam !== 'all') list = list.filter(t => t.team === tailorFilterTeam);
    if (tailorFilterStatus === 'active') list = list.filter(t => t.is_active !== false);
    else if (tailorFilterStatus === 'inactive') list = list.filter(t => t.is_active === false);
    list.sort((a, b) => {
      const aVal = (a[tailorSortBy] || '').toLowerCase();
      const bVal = (b[tailorSortBy] || '').toLowerCase();
      if (aVal < bVal) return tailorSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return tailorSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  })();

  const toggleTailorSort = (field) => {
    if (tailorSortBy === field) {
      setTailorSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTailorSortBy(field);
      setTailorSortDir('asc');
    }
  };

  const [tailorWorkloads, setTailorWorkloads] = useState([]);
  const [patternWorkloads, setPatternWorkloads] = useState([]);
  const [workloadsLoading, setWorkloadsLoading] = useState(false);

  const [autoAssignOpen, setAutoAssignOpen] = useState(false);
  const [autoAssignPreview, setAutoAssignPreview] = useState(null);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);

  // Production task tracker state
  const [productionTasks, setProductionTasks] = useState([]);
  const [prodTasksLoading, setProdTasksLoading] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [includeCompletedTasks, setIncludeCompletedTasks] = useState(false);
  const [taskTeamFilter, setTaskTeamFilter] = useState('all');
  const [taskTailorFilter, setTaskTailorFilter] = useState('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [editingProdTask, setEditingProdTask] = useState(null);
  const [prodTaskForm, setProdTaskForm] = useState({});
  const [prodTaskSaving, setProdTaskSaving] = useState(false);
  // Inline cell editing
  const [editingCell, setEditingCell] = useState(null); // { task_id, field }
  const [editingCellValue, setEditingCellValue] = useState('');
  // Bulk selection
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Tailor group expanded state
  const [expandedTailorGroups, setExpandedTailorGroups] = useState(new Set());
  const [reorderingTaskId, setReorderingTaskId] = useState(null);

  const [workloadAdHocOpen, setWorkloadAdHocOpen] = useState(false);
  const [workloadAdHocForm, setWorkloadAdHocForm] = useState({
    garment_no: '',
    task_key: '',
    estimated_minutes: '',
    status: 'not_started',
    notes: '',
    assigned_tailor_id: '',
    start_datetime: '',
    end_datetime: '',
  });
  const [workloadAdHocTaskOptions, setWorkloadAdHocTaskOptions] = useState([]);
  const [workloadAdHocContext, setWorkloadAdHocContext] = useState(null);
  const [workloadAdHocOptionsLoading, setWorkloadAdHocOptionsLoading] = useState(false);
  const [workloadAdHocRecommendation, setWorkloadAdHocRecommendation] = useState(null);
  const [workloadAdHocLoading, setWorkloadAdHocLoading] = useState(false);
  const [workloadAdHocSaving, setWorkloadAdHocSaving] = useState(false);

  const plannedTasksByGarment = useMemo(() => {
    const map = {};

    productionTasks.forEach(task => {
      const garmentId = task.associated_entity_id;
      if (!garmentId) return;
      if (!map[garmentId]) map[garmentId] = [];
      map[garmentId].push(task);
    });

    Object.entries(garmentPlanStatusByGarment || {}).forEach(([garmentId, taskList]) => {
      map[garmentId] = taskList || [];
    });

    Object.values(map).forEach(taskList => {
      taskList.sort((a, b) => {
        const seqA = Number(a.sequence ?? 9999);
        const seqB = Number(b.sequence ?? 9999);
        if (seqA !== seqB) return seqA - seqB;
        return (a.title || '').localeCompare(b.title || '');
      });
    });

    return map;
  }, [productionTasks, garmentPlanStatusByGarment]);

  const capabilityOptions = useMemo(() => {
    const optionMap = new Map(baseTailorCapabilities.map(item => [item.value, item]));
    taskConfigs.forEach(config => {
      (config.tasks || []).forEach(task => {
        const cap = (task.required_capability || '').trim();
        if (!cap) return;
        if (!optionMap.has(cap)) {
          optionMap.set(cap, {
            value: cap,
            label: cap
              .split('_')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '),
          });
        }
      });
    });
    return Array.from(optionMap.values());
  }, [taskConfigs]);

  const accessibleStoreOptions = useMemo(() => {
    const allOptions = (stores || [])
      .filter(store => store?.store_id)
      .map(store => ({ value: store.store_id, label: store.name || store.store_id }));

    if (!currentUser) return allOptions;
    if (['owner', 'admin'].includes(currentUser.role)) return allOptions;

    const assigned = currentUser.assigned_stores || [];
    if (!assigned.length) return [];

    const optionMap = new Map(allOptions.map(option => [option.value, option]));
    return assigned
      .filter(Boolean)
      .map(storeId => optionMap.get(storeId) || ({ value: storeId, label: storeId }));
  }, [stores, currentUser]);

  const showStoreQuickFilter = accessibleStoreOptions.length > 1;

  const garmentTypeFilterOptions = useMemo(() => {
    const values = new Set();
    (garmentTypeOptions || []).forEach(value => { if (value) values.add(value); });
    (garments || []).forEach(garment => {
      const value = garment?.garment_type;
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  }, [garmentTypeOptions, garments]);

  // ── Production task tracker helpers ────────────────────────────────────────
  const PROD_STATUS_OPTIONS = [
    { value: 'not_started', label: 'Not Started', cls: 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 hover:border-slate-300' },
    { value: 'in_progress', label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300' },
    { value: 'on_hold',     label: 'On Hold',     cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300' },
    { value: 'completed',   label: 'Completed',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300' },
  ];

  const getProdStatusInfo = (status) =>
    PROD_STATUS_OPTIONS.find(s => s.value === status) || PROD_STATUS_OPTIONS[0];

  const formatMinutes = (mins) => {
    if (mins == null || mins === '') return '—';
    const m = Number(mins);
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const taskFilterTailors = tailors.filter(t =>
    taskTeamFilter === 'all' || t.team === taskTeamFilter
  );

  const filteredProductionTasks = (() => {
    const searchLower = taskSearch.trim().toLowerCase();
    return productionTasks.filter(task => {
      if (searchLower) {
        const combined = `${task.order_number || ''}-${task.garment_no || ''}`.toLowerCase();
        const matchesSearch =
          combined.includes(searchLower) ||
          (task.order_number || '').toLowerCase().includes(searchLower) ||
          (task.garment_no   || '').toLowerCase().includes(searchLower) ||
          (task.customer_name || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      if (taskTailorFilter !== 'all' && task.assigned_tailor_id !== taskTailorFilter) return false;
      if (taskTeamFilter !== 'all') {
        const tailor = tailors.find(t => t.tailor_id === task.assigned_tailor_id);
        if (!tailor || tailor.team !== taskTeamFilter) return false;
      }
      if (!includeCompletedTasks && task.status === 'completed') return false;
      if (taskStatusFilter !== 'all' && task.status !== taskStatusFilter) return false;
      return true;
    });
  })();
  // ───────────────────────────────────────────────────────────────────────────

  // Fetch accessible stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const storesData = await getCachedAccessibleStores();
        setStores(Array.isArray(storesData) ? storesData : []);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getCurrentUser();
        const userData = response.data;
        setCurrentUser(userData);
        const hasAccess = ['owner', 'admin'].includes(userData.role) || userData.page_permissions?.garment_scheduler;
        setHasProductionAccess(!!hasAccess);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    fetchUser();
  }, []);

  // Fetch garments with pagination
  const fetchGarments = useCallback(async (pageNum = 1, append = false) => {
    const requestId = garmentsFetchSeqRef.current + 1;
    garmentsFetchSeqRef.current = requestId;

    if (garmentsFetchAbortRef.current) {
      garmentsFetchAbortRef.current.abort();
    }

    const abortController = new AbortController();
    garmentsFetchAbortRef.current = abortController;
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const params = {
        page: pageNum,
        page_size: PAGE_SIZE,
        lite: true,
      };
      
      // Add filters
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.garmentType !== 'all') params.garment_type = filters.garmentType;
      if (filters.patternStatus !== 'all') params.pattern_status = filters.patternStatus;
      if (filters.patternMaker !== 'all') params.pattern_maker = filters.patternMaker;
      if (filters.store !== 'all') params.store = filters.store;
      if (filters.dispatchDate) params.planned_dispatch_date = filters.dispatchDate;
      if (noDispatchFilter) params.no_dispatch = '1';
      if (noPatternFilter) params.no_pattern = '1';
      if (debouncedSearch) params.search = debouncedSearch;
      
      const response = await getGarmentsPaginated(params, { signal: abortController.signal });

      if (requestId !== garmentsFetchSeqRef.current) {
        return;
      }

      const { garments: data, total: totalCount, total_pages } = response.data;
      if (append) {
        setGarments(prev => [...prev, ...data]);
      } else {
        setGarments(data);
        setGarmentDetailsById({});
      }
      
      setTotal(totalCount);
      setHasMore(pageNum < total_pages); // Check if there are more pages
      setPage(pageNum);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      toast.error('Failed to fetch garments');
    } finally {
      if (garmentsFetchAbortRef.current === abortController) {
        garmentsFetchAbortRef.current = null;
      }
      if (requestId === garmentsFetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filters, debouncedSearch, noDispatchFilter, noPatternFilter]);

  useEffect(() => {
    return () => {
      if (garmentsFetchAbortRef.current) {
        garmentsFetchAbortRef.current.abort();
      }
    };
  }, []);

  const fetchDispatchDateOptions = useCallback(async () => {
    try {
      setDispatchDateOptionsLoading(true);
      const response = await getGarmentDispatchDateOptions({ limit: 8 });
      setDispatchDateOptions(response.data?.dates || []);
    } catch (error) {
      setDispatchDateOptions([]);
    } finally {
      setDispatchDateOptionsLoading(false);
    }
  }, []);

  const fetchGarmentPlanStatus = useCallback(async (garmentIds = []) => {
    try {
      const ids = (garmentIds || []).filter(Boolean);
      if (ids.length === 0) {
        setGarmentPlanStatusByGarment({});
        return;
      }
      const response = await getGarmentPlanStatus(ids);
      setGarmentPlanStatusByGarment(response.data?.by_garment || {});
    } catch (error) {
      setGarmentPlanStatusByGarment({});
    }
  }, []);

  const loadProductionOverview = async () => {
    try {
      const response = await getProductionOverview();
      setProductionOverview(response.data);
    } catch (error) {
      console.error('Failed to load production overview');
    }
  };

  const fetchTailors = async () => {
    try {
      setTailorsLoading(true);
      const response = await getTailors();
      setTailors(response.data);
    } catch (error) {
      toast.error('Failed to load tailors');
    } finally {
      setTailorsLoading(false);
    }
  };

  const fetchTaskConfigs = async () => {
    try {
      const response = await getGarmentTaskConfigs();
      setTaskConfigs(response.data);
    } catch (error) {
      toast.error('Failed to load garment task configs');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const fetchTailorWorkloads = async () => {
    try {
      setWorkloadsLoading(true);
      const response = await getTailorWorkloads();
      setTailorWorkloads(response.data);
    } catch (error) {
      toast.error('Failed to load tailor workloads');
    } finally {
      setWorkloadsLoading(false);
    }
  };

  const fetchPatternWorkloads = async () => {
    try {
      setWorkloadsLoading(true);
      const response = await getPatternWorkloads();
      setPatternWorkloads(response.data);
    } catch (error) {
      toast.error('Failed to load pattern workloads');
    } finally {
      setWorkloadsLoading(false);
    }
  };

  const handleAutoAssignPreview = async () => {
    try {
      setAutoAssignLoading(true);
      const response = await autoAssignProduction(false);
      setAutoAssignPreview(response.data);
      setAutoAssignOpen(true);
    } catch (error) {
      toast.error('Failed to generate auto-assign preview');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  const fetchProductionTasks = async ({ includeCompleted = false } = {}) => {
    setProdTasksLoading(true);
    try {
      const response = await getProductionTasks({ include_completed: includeCompleted });
      setProductionTasks(response.data);
    } catch {
      toast.error('Failed to load production tasks');
    }
    setProdTasksLoading(false);
  };

  // Inline cell save — called on blur or dropdown change
  const saveCellUpdate = async (task_id, payload) => {
    try {
      const response = await updateProductionTask(task_id, payload);
      setProductionTasks(prev => prev.map(t => t.task_id === task_id ? { ...t, ...response.data } : t));
    } catch {
      toast.error('Failed to save');
    }
    setEditingCell(null);
  };

  const startCellEdit = (task_id, field, currentValue) => {
    setEditingCell({ task_id, field });
    setEditingCellValue(currentValue ?? '');
  };

  const handleCellBlur = (task_id, field) => {
    const val = editingCellValue;
    if (field === 'actual_minutes') {
      saveCellUpdate(task_id, { actual_minutes: val === '' ? null : parseInt(val, 10), is_time_overridden: val !== '' });
    } else if (field === 'start_datetime' || field === 'end_datetime') {
      saveCellUpdate(task_id, { [field]: val || null, is_time_overridden: false });
    } else {
      saveCellUpdate(task_id, { [field]: val || null });
    }
  };

  // Bulk selection helpers
  const toggleTaskSelection = (task_id) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      next.has(task_id) ? next.delete(task_id) : next.add(task_id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTaskIds.size === filteredProductionTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(filteredProductionTasks.map(t => t.task_id)));
    }
  };

  const handleBulkDeleteTasks = async () => {
    if (selectedTaskIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await bulkDeleteProductionTasks([...selectedTaskIds]);
      setProductionTasks(prev => prev.filter(t => !selectedTaskIds.has(t.task_id)));
      setSelectedTaskIds(new Set());
      toast.success(`Deleted ${selectedTaskIds.size} task(s)`);
    } catch {
      toast.error('Failed to delete tasks');
    }
    setBulkDeleting(false);
  };

  // ── Queue reorder ──────────────────────────────────────────────────────────
  const handleMoveTask = async (task_id, direction) => {
    setReorderingTaskId(task_id);
    // Optimistic: swap queue_positions in local state
    setProductionTasks(prev => {
      const tailor_id = prev.find(t => t.task_id === task_id)?.assigned_tailor_id;
      if (!tailor_id) return prev;
      const group = prev
        .filter(t => t.assigned_tailor_id === tailor_id)
        .sort((a, b) => (a.queue_position ?? 9999) - (b.queue_position ?? 9999));
      const idx = group.findIndex(t => t.task_id === task_id);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= group.length) return prev;
      const next = [...prev];
      const aIdx = next.findIndex(t => t.task_id === group[idx].task_id);
      const bIdx = next.findIndex(t => t.task_id === group[swapIdx].task_id);
      const posA = next[aIdx].queue_position;
      const posB = next[bIdx].queue_position;
      next[aIdx] = { ...next[aIdx], queue_position: posB };
      next[bIdx] = { ...next[bIdx], queue_position: posA };
      return next;
    });
    try {
      await moveProductionTask(task_id, direction);
    } catch {
      toast.error('Failed to reorder task');
      await fetchProductionTasks(); // revert on failure
    }
    setReorderingTaskId(null);
  };

  // ── Blocked check ──────────────────────────────────────────────────────────
  const isTaskBlocked = (task) => {
    if (!task.sequence || task.sequence <= 1) return false;
    return productionTasks.some(t =>
      t.associated_entity_id === task.associated_entity_id &&
      (t.sequence ?? 0) < task.sequence &&
      t.status !== 'completed'
    );
  };

  // ── Grouped-by-tailor derived data ─────────────────────────────────────────
  const tasksByTailor = (() => {
    const groups = {};
    const unassigned = [];
    filteredProductionTasks.forEach(task => {
      if (!task.assigned_tailor_id) {
        unassigned.push(task);
      } else {
        if (!groups[task.assigned_tailor_id]) {
          const tailor = tailors.find(t => t.tailor_id === task.assigned_tailor_id);
          // Fall back to the stored name if tailors haven't loaded yet
          const fallbackName = Array.isArray(task.assigned_to) ? task.assigned_to[0] : task.assigned_to;
          groups[task.assigned_tailor_id] = {
            tailor: tailor || { tailor_id: task.assigned_tailor_id, name: fallbackName || 'Unknown Tailor' },
            tasks: [],
          };
        }
        groups[task.assigned_tailor_id].tasks.push(task);
      }
    });
    Object.values(groups).forEach(g => {
      g.tasks.sort((a, b) => (a.queue_position ?? 9999) - (b.queue_position ?? 9999));
    });
    return { groups: Object.values(groups), unassigned };
  })();

  const handleApplyAutoAssign = async () => {
    try {
      setAutoAssignLoading(true);
      const response = await autoAssignProduction(true);
      setAutoAssignPreview(response.data);
      toast.success('Auto-assignment applied');
      await loadProductionOverview();
      if (activeTab === 'tailor_workload') {
        await fetchProductionTasks({ includeCompleted: includeCompletedTasks });
      }
      if (activeTab === 'pattern_workload') {
        await fetchPatternWorkloads();
      }
    } catch (error) {
      toast.error('Failed to apply auto-assign');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  // Reset and fetch when filters or search changes
  useEffect(() => {
    setPage(1);
    fetchGarments(1, false);
  }, [filters, debouncedSearch, noDispatchFilter, noPatternFilter]);

  useEffect(() => {
    if (!hasProductionAccess) {
      setActiveTab('garments');
    }
  }, [hasProductionAccess]);

  useEffect(() => {
    if (!hasProductionAccess) return;
    loadProductionOverview();
  }, [hasProductionAccess]);

  useEffect(() => {
    if (!hasProductionAccess) return;
    if (activeTab === 'tailors') {
      fetchTailors();
      fetchTaskConfigs();
    }
    if (activeTab === 'tailor_workload') {
      // Always fetch both so task view always has current tailor data for grouping
      fetchProductionTasks({ includeCompleted: includeCompletedTasks });
      fetchTailors();
      fetchTaskConfigs();
    }
    if (activeTab === 'pattern_workload') {
      fetchPatternWorkloads();
    }
    if (activeTab === 'task_configs') {
      fetchTaskConfigs();
      fetchProducts();
    }
  }, [activeTab, hasProductionAccess, includeCompletedTasks]);

  useEffect(() => {
    if (activeTab !== 'garments') return;
    fetchDispatchDateOptions();
  }, [activeTab, fetchDispatchDateOptions]);

  useEffect(() => {
    if (activeTab !== 'garments') return;
    fetchGarmentPlanStatus((garments || []).map(g => g.garment_id));
  }, [activeTab, garments, fetchGarmentPlanStatus]);
  
  // SSE Real-time updates
  useEffect(() => {
    const backendUrl = getApiBaseUrl();
    const token = localStorage.getItem('token'); // Fixed: was 'access_token', should be 'token'
    
    if (!token) {
      console.warn('⚠️ No auth token found, skipping SSE connection');
      return;
    }
    
    // Create EventSource for SSE
    const sseUrl = `${backendUrl}/api/garments/stream?token=${token}`;
    const eventSource = new EventSource(sseUrl);
    
    eventSource.addEventListener('connected', (event) => {
      console.log('✅ Connected to garments stream');
    });
    
    eventSource.addEventListener('garment_update', (event) => {
      try {
        const payload = JSON.parse(event.data);
        const { updates } = payload;
        
        console.log(`📡 Received ${updates.length} update(s) from SSE`);
        
        // Apply updates to local state
        setGarments(prevGarments => {
          const updatedGarments = [...prevGarments];
          
          updates.forEach(update => {
            const { type, data } = update;
            const garmentIndex = updatedGarments.findIndex(g => g.garment_id === data.garment_id);
            
            if (garmentIndex !== -1) {
              // Update existing garment
              if (type === 'garment_updated') {
                updatedGarments[garmentIndex] = {
                  ...updatedGarments[garmentIndex],
                  ...data.changes
                };
                console.log(`✅ Updated garment ${data.garment_id}`);
              } else if (type === 'status_updated') {
                updatedGarments[garmentIndex] = {
                  ...updatedGarments[garmentIndex],
                  garment_status: data.garment_status
                };
                console.log(`✅ Updated status for ${data.garment_id} to ${data.garment_status}`);
              }
            }
          });
          
          return updatedGarments;
        });
      } catch (error) {
        console.error('❌ Error processing garment update:', error);
      }
    });
    
    eventSource.onerror = (error) => {
      console.error('❌ SSE connection error:', error);
      // EventSource will automatically try to reconnect
    };
    
    // Cleanup on unmount
    return () => {
      eventSource.close();
      console.log('🔌 Disconnected from garments stream');
    };
  }, []); // Empty deps - only connect once
  
  // Load more handler
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchGarments(page + 1, true);
    }
  };

  const buildGarmentUpdatePayload = (garment, overrides = {}) => ({
    order_id: garment.order_id,
    garment_type: overrides.garment_type !== undefined ? overrides.garment_type : garment.garment_type,
    priority: overrides.priority !== undefined ? overrides.priority : garment.priority,
    pattern_no: overrides.pattern_no !== undefined ? overrides.pattern_no : garment.pattern_no,
    pattern_status: overrides.pattern_status !== undefined ? overrides.pattern_status : garment.pattern_status,
    pattern_maker: overrides.pattern_maker !== undefined ? overrides.pattern_maker : garment.pattern_maker,
    planned_dispatch_date: overrides.planned_dispatch_date !== undefined ? overrides.planned_dispatch_date : garment.planned_dispatch_date,
    order_sheet_status: overrides.order_sheet_status !== undefined ? overrides.order_sheet_status : garment.order_sheet_status,
    remarks: overrides.remarks !== undefined ? overrides.remarks : garment.remarks,
    is_test_garment: overrides.is_test_garment !== undefined ? overrides.is_test_garment : garment.is_test_garment,
    notes: overrides.notes !== undefined ? overrides.notes : garment.notes,
    garment_status: overrides.garment_status !== undefined ? overrides.garment_status : garment.garment_status,
    internal_delivery_date: overrides.internal_delivery_date !== undefined
      ? overrides.internal_delivery_date
      : (garment.internal_delivery_date || null),
    client_final_date: overrides.client_final_date !== undefined
      ? overrides.client_final_date
      : (garment.client_final_date || null),
  });

  const openDeliveryDateEditor = (garment) => {
    setDeliveryDateGarment(garment);
    setDeliveryDateForm({
      internal_delivery_date: garment.internal_delivery_date ? String(garment.internal_delivery_date).split('T')[0] : '',
      client_final_date: garment.client_final_date ? String(garment.client_final_date).split('T')[0] : '',
    });
    setDeliveryDateEditorOpen(true);
  };

  const saveGarmentDeliveryDates = async () => {
    if (!deliveryDateGarment) return;
    setDeliveryDateSaving(true);
    try {
      await updateGarment(
        deliveryDateGarment.garment_id,
        buildGarmentUpdatePayload(deliveryDateGarment, {
          internal_delivery_date: deliveryDateForm.internal_delivery_date || null,
          client_final_date: deliveryDateForm.client_final_date || null,
        })
      );

      setGarments(prev => prev.map(g => (
        g.garment_id === deliveryDateGarment.garment_id
          ? {
              ...g,
              internal_delivery_date: deliveryDateForm.internal_delivery_date || null,
              client_final_date: deliveryDateForm.client_final_date || null,
            }
          : g
      )));

      if (expandedRow?.garment_id === deliveryDateGarment.garment_id) {
        setExpandedRow(prev => prev ? {
          ...prev,
          internal_delivery_date: deliveryDateForm.internal_delivery_date || null,
          client_final_date: deliveryDateForm.client_final_date || null,
        } : prev);
      }

      toast.success('Garment IDD/CDD updated');
      setDeliveryDateEditorOpen(false);
      setDeliveryDateGarment(null);
    } catch (error) {
      toast.error('Failed to update garment IDD/CDD');
    } finally {
      setDeliveryDateSaving(false);
    }
  };

  // Debounced field update for notes and pattern_no
  const debouncedFieldUpdate = useDebouncedCallback(async (garmentId, field, value, garment) => {
    try {
      await updateGarment(garmentId, buildGarmentUpdatePayload(garment, {
        priority: field === 'priority' ? value : garment.priority,
        pattern_no: field === 'pattern_no' ? value : garment.pattern_no,
        pattern_status: field === 'pattern_status' ? value : garment.pattern_status,
        pattern_maker: field === 'pattern_maker' ? (value === '__none__' ? null : value) : garment.pattern_maker,
        planned_dispatch_date: field === 'planned_dispatch_date' ? value : garment.planned_dispatch_date,
        order_sheet_status: field === 'order_sheet_status' ? value : garment.order_sheet_status,
        notes: field === 'notes' ? value : garment.notes,
      }));
      toast.success('Updated successfully');
    } catch (error) {
      toast.error('Failed to update');
    }
  }, 500);

  // Handle inline field updates
  const handleFieldUpdate = async (garmentId, field, value) => {
    const garment = garments.find(g => g.garment_id === garmentId);
    if (!garment) return;

    // Update local state immediately for responsive UI
    setGarments(prev => prev.map(g => 
      g.garment_id === garmentId ? { ...g, [field]: value } : g
    ));

    // For text inputs (notes, pattern_no), use debounced update
    if (field === 'notes' || field === 'pattern_no') {
      debouncedFieldUpdate(garmentId, field, value, { ...garment, [field]: value });
      return;
    }

    // For status, use dedicated endpoint immediately
    try {
      if (field === 'garment_status') {
        await updateGarmentStatus(garmentId, value);
      } else {
        await updateGarment(garmentId, buildGarmentUpdatePayload(garment, {
          priority: field === 'priority' ? value : garment.priority,
          pattern_status: field === 'pattern_status' ? value : garment.pattern_status,
          pattern_maker: field === 'pattern_maker' ? (value === '__none__' ? null : value) : garment.pattern_maker,
          planned_dispatch_date: field === 'planned_dispatch_date' ? value : garment.planned_dispatch_date,
          order_sheet_status: field === 'order_sheet_status' ? value : garment.order_sheet_status,
        }));
      }
      toast.success('Updated successfully');
    } catch (error) {
      toast.error('Failed to update');
      // Revert on error
      setGarments(prev => prev.map(g => 
        g.garment_id === garmentId ? garment : g
      ));
    }
  };

  // Handle bulk selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedGarments(garments.map(g => g.garment_id));
    } else {
      setSelectedGarments([]);
    }
  };

  const handleSelectGarment = (garmentId, checked) => {
    if (checked) {
      setSelectedGarments(prev => [...prev, garmentId]);
    } else {
      setSelectedGarments(prev => prev.filter(id => id !== garmentId));
    }
  };

  // Bulk update selected garments
  const handleBulkUpdate = async (field, value) => {
    if (selectedGarments.length === 0) return;
    const count = selectedGarments.length;
    try {
      // Update local state immediately
      setGarments(prev => prev.map(g =>
        selectedGarments.includes(g.garment_id) ? { ...g, [field]: value } : g
      ));

      // Fire API calls
      if (field === 'garment_status') {
        await Promise.all(selectedGarments.map(id => updateGarmentStatus(id, value)));
      } else {
        await Promise.all(selectedGarments.map(id => {
          const garment = garments.find(g => g.garment_id === id);
          if (!garment) return Promise.resolve();
          return updateGarment(id, buildGarmentUpdatePayload(garment, {
            priority: field === 'priority' ? value : garment.priority,
            pattern_status: field === 'pattern_status' ? value : garment.pattern_status,
            pattern_maker: field === 'pattern_maker' ? (value === '__none__' ? null : value) : garment.pattern_maker,
            planned_dispatch_date: field === 'planned_dispatch_date' ? value : garment.planned_dispatch_date,
          }));
        }));
      }
      toast.success(`Updated ${field.replace(/_/g, ' ')} for ${count} garment(s)`);
    } catch (error) {
      toast.error('Some updates failed');
      fetchGarments(page, false);
    }
  };

  // Handle delete
  const handleDelete = async (garmentId) => {
    if (!window.confirm('Are you sure you want to delete this garment?')) return;
    try {
      await deleteGarment(garmentId);
      toast.success('Garment deleted');
      setGarments(prev => prev.filter(g => g.garment_id !== garmentId));
      setTotal(prev => prev - 1);
    } catch (error) {
      toast.error('Failed to delete garment');
    }
  };

  const resetTailorForm = () => {
    setEditingTailor(null);
    setTailorForm({
      name: '',
      employee_id: '',
      team: '',
      primary_task: '',
      capabilities: [],
      primary_garment_types: [],
      secondary_garment_types: [],
      is_active: true,
    });
  };

  const handleOpenTailorDialog = (tailor = null) => {
    if (tailor) {
      setEditingTailor(tailor);
      setTailorForm({
        name: tailor.name || '',
        employee_id: tailor.employee_id || '',
        team: tailor.team || '',
        primary_task: tailor.primary_task || '',
        capabilities: tailor.capabilities || [],
        primary_garment_types: tailor.primary_garment_types || [],
        secondary_garment_types: tailor.secondary_garment_types || [],
        is_active: tailor.is_active !== false,
      });
    } else {
      resetTailorForm();
    }
    setTailorDialogOpen(true);
  };

  const handleSaveTailor = async () => {
    if (!tailorForm.name) {
      toast.error('Tailor name is required');
      return;
    }
    try {
      if (editingTailor) {
        await updateTailor(editingTailor.tailor_id, tailorForm);
        toast.success('Tailor updated');
      } else {
        await createTailor(tailorForm);
        toast.success('Tailor created');
      }
      setTailorDialogOpen(false);
      resetTailorForm();
      fetchTailors();
    } catch (error) {
      toast.error('Failed to save tailor');
    }
  };

  const toggleTailorCapability = (capability) => {
    setTailorForm(prev => {
      const capabilities = prev.capabilities || [];
      const exists = capabilities.includes(capability);
      return {
        ...prev,
        capabilities: exists ? capabilities.filter(c => c !== capability) : [...capabilities, capability]
      };
    });
  };

  const GARMENT_TYPE_OPTIONS = [
    { value: 'jacket',    label: 'Jacket' },
    { value: 'overcoat',  label: 'Overcoat' },
    { value: 'waistcoat', label: 'Waistcoat' },
    { value: 'trouser',   label: 'Trouser' },
    { value: 'shirt',     label: 'Shirt' },
    { value: 'test_jacket',    label: 'Test Jacket' },
    { value: 'test_overcoat',  label: 'Test Overcoat' },
    { value: 'test_waistcoat', label: 'Test Waistcoat' },
    { value: 'test_trouser',   label: 'Test Trouser' },
    { value: 'test_shirt',     label: 'Test Shirt' },
  ];

  const toggleGarmentType = (field, value) => {
    setTailorForm(prev => {
      const list = prev[field] || [];
      return { ...prev, [field]: list.includes(value) ? list.filter(v => v !== value) : [...list, value] };
    });
  };

  const handleDeleteTailor = async (tailorId) => {
    if (!window.confirm('Delete this tailor?')) return;
    try {
      await deleteTailor(tailorId);
      toast.success('Tailor deleted');
      fetchTailors();
    } catch (error) {
      toast.error('Failed to delete tailor');
    }
  };

  const toggleTailorSelection = (tailorId) => {
    setSelectedTailorIds(prev => {
      const next = new Set(prev);
      next.has(tailorId) ? next.delete(tailorId) : next.add(tailorId);
      return next;
    });
  };

  const toggleSelectAllTailors = () => {
    if (selectedTailorIds.size === displayedTailors.length) {
      setSelectedTailorIds(new Set());
    } else {
      setSelectedTailorIds(new Set(displayedTailors.map(t => t.tailor_id)));
    }
  };

  const handleApplyTailorBulkEdit = async () => {
    if (selectedTailorIds.size === 0) return;
    setTailorBulkSaving(true);
    try {
      const payload = {
        tailor_ids: [...selectedTailorIds],
        action: tailorBulkField === 'is_active' ? 'set' : tailorBulkAction,
        field: tailorBulkField,
        value: tailorBulkField === 'is_active' ? [] : tailorBulkValues,
        bool_value: tailorBulkField === 'is_active' ? tailorBulkBoolValue : null,
      };
      const res = await bulkUpdateTailors(payload);
      toast.success(`Updated ${res.data.updated} tailor(s)`);
      setTailorBulkEditOpen(false);
      setTailorBulkValues([]);
      setSelectedTailorIds(new Set());
      fetchTailors();
    } catch (error) {
      toast.error('Bulk update failed');
    }
    setTailorBulkSaving(false);
  };

  // ── Assignment Planning ────────────────────────────────────────────────────
  const handleOpenAssignModal = async () => {
    if (selectedGarments.length === 0) return;
    setPlanPreviewLoading(true);
    setAssignModalOpen(true);
    try {
      await fetchTailors();
      const res = await planPreview(selectedGarments);
      setPlanPreviewData(res.data);
      // Pre-populate draftAssignments with recommendations
      const draft = {};
      res.data.garments.forEach(g => {
        g.tasks.forEach(t => {
          if (t.recommended_tailor_id) {
            draft[`${g.garment_id}__${t.task_key}`] = t.recommended_tailor_id;
          }
        });
      });
      setDraftAssignments(draft);
    } catch {
      toast.error('Failed to load assignment plan');
      setAssignModalOpen(false);
    }
    setPlanPreviewLoading(false);
  };

  // Compute live load = base load + pending draft assignments
  const computeLiveLoad = (planData, draft) => {
    if (!planData) return {};
    const live = { ...planData.tailor_loads };
    planData.garments.forEach(g => {
      g.tasks.forEach(t => {
        const key = `${g.garment_id}__${t.task_key}`;
        const tid = draft[key];
        if (tid) {
          live[tid] = (live[tid] || 0) + (t.estimated_minutes || 0);
        }
      });
    });
    return live;
  };

  const handleConfirmAssignments = async () => {
    if (!planPreviewData) return;
    setApplyingAssignments(true);
    const assignments = [];
    planPreviewData.garments.forEach(g => {
      g.tasks.forEach(t => {
        const key = `${g.garment_id}__${t.task_key}`;
        const tid = draftAssignments[key];
        if (tid) {
          assignments.push({
            garment_id: g.garment_id,
            task_key: t.task_key,
            task_type: t.task_type,
            label: t.label,
            sequence: t.sequence,
            estimated_minutes: t.estimated_minutes,
            tailor_id: tid,
            garment_no: g.garment_no,
            garment_type: g.garment_type,
            customer_name: g.customer_name,
            priority: g.priority,
            due_date: g.planned_dispatch_date,
          });
        }
      });
    });
    try {
      const res = await applyAssignments(assignments);
      const created = Number(res.data?.created || 0);
      const skippedExisting = Number(res.data?.skipped_existing || 0);
      const skippedPayloadDuplicates = Number(res.data?.skipped_payload_duplicates || 0);

      if (created > 0 && skippedExisting === 0 && skippedPayloadDuplicates === 0) {
        toast.success(`${created} task${created !== 1 ? 's' : ''} assigned successfully`);
      } else if (created > 0) {
        toast.success(`${created} task${created !== 1 ? 's' : ''} created, ${skippedExisting} already existed`);
      } else {
        toast.info(`No new tasks created. ${skippedExisting} task${skippedExisting !== 1 ? 's' : ''} already existed.`);
      }

      setAssignModalOpen(false);
      setSelectedGarments([]);
      setPlanPreviewData(null);
      setDraftAssignments({});
      // Refresh production tasks if workload tab is visible
      if (activeTab === 'tailor_workload') {
        fetchProductionTasks({ includeCompleted: includeCompletedTasks });
      }
    } catch {
      toast.error('Failed to apply assignments');
    }
    setApplyingAssignments(false);
  };

  const openWorkloadAdHocModal = () => {
    setWorkloadAdHocForm({
      garment_no: '',
      task_key: '',
      estimated_minutes: '',
      status: 'not_started',
      notes: '',
      assigned_tailor_id: '',
      start_datetime: '',
      end_datetime: '',
    });
    setWorkloadAdHocTaskOptions([]);
    setWorkloadAdHocContext(null);
    setWorkloadAdHocRecommendation(null);
    setWorkloadAdHocOpen(true);
  };

  const handleLoadWorkloadAdHocOptions = async () => {
    if (!workloadAdHocForm.garment_no) {
      toast.error('Garment no is required');
      return;
    }
    setWorkloadAdHocOptionsLoading(true);
    try {
      const res = await getProductionAdHocOptions(workloadAdHocForm.garment_no);
      const manualTasks = res.data.manual_tasks || [];
      setWorkloadAdHocContext(res.data.garment || null);
      setWorkloadAdHocTaskOptions(manualTasks);
      if (manualTasks.length === 0) {
        toast.error('No manual/ad-hoc task configs found for this garment type');
      } else {
        const first = manualTasks[0];
        setWorkloadAdHocForm(prev => ({
          ...prev,
          task_key: first.task_key,
          estimated_minutes: String(first.estimated_minutes ?? ''),
        }));
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load ad-hoc tasks');
      setWorkloadAdHocTaskOptions([]);
      setWorkloadAdHocContext(null);
    }
    setWorkloadAdHocOptionsLoading(false);
  };

  const handleRecommendWorkloadAdHocTask = async () => {
    if (!workloadAdHocForm.garment_no || !workloadAdHocForm.task_key) {
      toast.error('Garment no and task are required for recommendation');
      return;
    }
    setWorkloadAdHocLoading(true);
    try {
      const res = await recommendProductionAdHocTask({
        garment_no: workloadAdHocForm.garment_no,
        task_key: workloadAdHocForm.task_key,
      });
      setWorkloadAdHocRecommendation(res.data);
      const selectedTask = res.data.task;
      setWorkloadAdHocForm(prev => ({
        ...prev,
        estimated_minutes: prev.estimated_minutes || String(selectedTask?.estimated_minutes ?? ''),
        assigned_tailor_id: res.data.recommended_tailor_id || prev.assigned_tailor_id,
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to get recommendation');
      setWorkloadAdHocRecommendation(null);
    }
    setWorkloadAdHocLoading(false);
  };

  const handleCreateWorkloadAdHocTask = async () => {
    if (!workloadAdHocForm.garment_no || !workloadAdHocForm.task_key) {
      toast.error('Garment no and task are required');
      return;
    }
    setWorkloadAdHocSaving(true);
    try {
      await createProductionAdHocTask({
        garment_no: workloadAdHocForm.garment_no,
        task_key: workloadAdHocForm.task_key,
        estimated_minutes: Number(workloadAdHocForm.estimated_minutes || 0),
        status: workloadAdHocForm.status || 'not_started',
        notes: workloadAdHocForm.notes || '',
        assigned_tailor_id: workloadAdHocForm.assigned_tailor_id || null,
        start_datetime: workloadAdHocForm.start_datetime || null,
        end_datetime: workloadAdHocForm.end_datetime || null,
      });
      toast.success('Ad-hoc task created');
      setWorkloadAdHocOpen(false);
      if (activeTab === 'tailor_workload') {
        fetchProductionTasks({ includeCompleted: includeCompletedTasks });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create ad-hoc task');
    }
    setWorkloadAdHocSaving(false);
  };

  const generateTaskKey = (label, fallback) => {
    if (!label) return fallback;
    const slug = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return slug || fallback;
  };

  const resetTaskConfigForm = () => {
    setEditingTaskConfig(null);
    setTaskConfigForm({
      garment_type: '',
      production_required: true,
      tasks: [],
    });
  };

  const handleOpenTaskConfigDialog = (config = null) => {
    if (config) {
      setEditingTaskConfig(config);
      setTaskConfigForm({
        garment_type: config.garment_type || '',
        production_required: config.production_required !== false,
        tasks: (config.tasks || []).map(task => ({
          task_key: task.task_key || predefinedTaskLibrary[0].key,
          label: task.label || predefinedTaskLibrary[0].label,
          task_category: task.task_category || predefinedTaskLibrary[0].category,
          planning_mode: task.planning_mode || 'auto',
          required_capability: task.required_capability || task.task_type || 'sewing',
          task_type: task.task_type || 'sewing',
          sequence: task.sequence || 1,
          estimated_minutes: task.estimated_minutes || 0,
        })),
      });
    } else {
      resetTaskConfigForm();
    }
    setTaskConfigDialogOpen(true);
  };

  const handleTaskConfigSave = async () => {
    if (!taskConfigForm.garment_type) {
      toast.error('Garment type is required');
      return;
    }
    const tasksPayload = taskConfigForm.tasks.map((task, index) => {
      const fromLibrary = predefinedTaskLibrary.find(t => t.key === task.task_key);
      const label = fromLibrary?.label || task.label?.trim() || `Task ${index + 1}`;
      const category = fromLibrary?.category || task.task_category || 'Sewing';
      const taskType = fromLibrary?.task_type || task.task_type || 'sewing';
      const requiredCapability = fromLibrary?.required_capability || task.required_capability || taskType;
      return {
        task_key: task.task_key || fromLibrary?.key || generateTaskKey(label, `task_${index + 1}`),
        label,
        task_category: category,
        planning_mode: task.planning_mode === 'manual' ? 'manual' : 'auto',
        required_capability: requiredCapability,
        task_type: taskType,
        sequence: index + 1,
        estimated_minutes: Number(task.estimated_minutes) || 0,
      };
    });

    try {
      const payload = {
        garment_type: taskConfigForm.garment_type,
        production_required: taskConfigForm.production_required,
        tasks: tasksPayload,
      };
      if (editingTaskConfig) {
        await updateGarmentTaskConfig(editingTaskConfig.config_id, payload);
        toast.success('Task config updated');
      } else {
        await createGarmentTaskConfig(payload);
        toast.success('Task config created');
      }
      setTaskConfigDialogOpen(false);
      resetTaskConfigForm();
      fetchTaskConfigs();
    } catch (error) {
      toast.error('Failed to save task config');
    }
  };

  const handleDeleteTaskConfig = async (configId) => {
    if (!window.confirm('Delete this task config?')) return;
    try {
      await deleteGarmentTaskConfig(configId);
      toast.success('Task config deleted');
      fetchTaskConfigs();
    } catch (error) {
      toast.error('Failed to delete task config');
    }
  };

  const handleSeedTaskConfigs = async () => {
    try {
      await seedGarmentTaskConfigs();
      toast.success('Default task configs seeded');
      fetchTaskConfigs();
    } catch (error) {
      toast.error('Failed to seed default task configs');
    }
  };

  const handleAddTaskRow = () => {
    const defaultTask = predefinedTaskLibrary[0];
    setTaskConfigForm(prev => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        {
          task_key: defaultTask.key,
          label: defaultTask.label,
          task_category: defaultTask.category,
          planning_mode: 'auto',
          required_capability: defaultTask.required_capability,
          task_type: defaultTask.task_type,
          sequence: prev.tasks.length + 1,
          estimated_minutes: 30,
        }
      ]
    }));
  };

  const handleUpdateTaskRow = (index, field, value) => {
    if (field === 'task_key') {
      const fromLibrary = predefinedTaskLibrary.find(t => t.key === value);
      setTaskConfigForm(prev => ({
        ...prev,
        tasks: prev.tasks.map((task, idx) => idx === index ? {
          ...task,
          task_key: value,
          label: fromLibrary?.label || task.label,
          task_category: fromLibrary?.category || task.task_category,
          task_type: fromLibrary?.task_type || task.task_type,
          required_capability: fromLibrary?.required_capability || task.required_capability,
        } : task),
      }));
      return;
    }

    if (field === 'task_category') {
      const firstInCategory = predefinedTaskLibrary.find(t => t.category === value);
      setTaskConfigForm(prev => ({
        ...prev,
        tasks: prev.tasks.map((task, idx) => idx === index ? {
          ...task,
          task_category: value,
          ...(firstInCategory ? {
            task_key: firstInCategory.key,
            label: firstInCategory.label,
            task_type: firstInCategory.task_type,
            required_capability: firstInCategory.required_capability,
          } : {}),
        } : task),
      }));
      return;
    }

    setTaskConfigForm(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, idx) => idx === index ? { ...task, [field]: value } : task),
    }));
  };

  const handleRemoveTaskRow = (index) => {
    setTaskConfigForm(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, idx) => idx !== index),
    }));
  };

  // Server-side filtering is now used, garments are already filtered
  // Get unique stores from garments for the filter dropdown
  // Now using dynamically fetched stores from API

  // Get status display
  const getStatusConfig = (status, type = 'garment') => {
    const normalizedStatus = type === 'pattern' && status === 'in_progress' ? 'for_approval' : status;
    const list = type === 'garment' ? garmentStatuses : patternStatuses;
    return list.find(s => s.value === normalizedStatus) || { label: normalizedStatus, color: 'bg-gray-500/20 text-gray-400' };
  };

  const getPriorityConfig = (priority) => {
    return priorities.find(p => p.value === priority) || { label: priority, color: '' };
  };

  const getCapabilityLabel = (capability) => {
    return capabilityOptions.find(c => c.value === capability)?.label || capability;
  };

  const getTaskTypeLabel = (taskType) => {
    return taskTypeOptions.find(t => t.value === taskType)?.label || taskType;
  };

  const getTaskTotalMinutes = (tasks) => {
    return (tasks || []).reduce((sum, task) => sum + Number(task.estimated_minutes || 0), 0);
  };
  
  const getFabricStatusConfig = (status) => {
    return fabricStatuses.find(s => s.value === status) || { label: status, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
  };

  // Handle order sheet PDF generation
  const handleGenerateOrderSheet = async (garment) => {
    // Check if garment has a pattern assigned
    if (!garment.pattern_no) {
      // Show warning dialog
      setGarmentPendingOrderSheet(garment);
      setNoPatternWarningOpen(true);
      return;
    }
    
    // Pattern exists, proceed with generation
    await generateOrderSheetPdf(garment);
  };
  
  // Actually generate the PDF (called after confirmation or if pattern exists)
  const generateOrderSheetPdf = async (garment) => {
    try {
      toast.info('Generating Order Sheet PDF...');
      const fileName = await generateOrderSheetForGarment(garment.garment_id);
      toast.success(`Order Sheet generated: ${fileName}`);
    } catch (error) {
      console.error('Error generating order sheet:', error);
      toast.error('Failed to generate Order Sheet PDF');
    }
  };
  
  // Handle user confirmation to proceed without pattern
  const handleProceedWithoutPattern = async () => {
    if (garmentPendingOrderSheet) {
      await generateOrderSheetPdf(garmentPendingOrderSheet);
      setNoPatternWarningOpen(false);
      setGarmentPendingOrderSheet(null);
    }
  };

  // Handle images
  const handleAddImages = (garment) => {
    setSelectedGarmentForImages(garment);
    setImageManagerOpen(true);
  };
  
  const handleImagesUpdated = () => {
    // Refresh garments to update image counts
    fetchGarments(1, false);
  };

  const ensureGarmentDetails = useCallback(async (garmentId) => {
    if (!garmentId || garmentDetailsById[garmentId]) {
      return;
    }

    try {
      const response = await getGarment(garmentId);
      const detail = response.data;
      setGarmentDetailsById((prev) => ({
        ...prev,
        [garmentId]: detail,
      }));
    } catch (error) {
      toast.error('Failed to load garment details');
    }
  }, [garmentDetailsById]);
  
  // Toggle row expansion (accordion behavior)
  const toggleRowExpansion = (garmentId) => {
    const nextExpanded = expandedRow === garmentId ? null : garmentId;
    setExpandedRow(nextExpanded);
    if (nextExpanded) {
      ensureGarmentDetails(garmentId);
    }
  };
  
  // Handle view customizations
  const handleViewCustomizations = (garment) => {
    setSelectedGarmentForCustomizations(garment);
    setCustomizationsOpen(true);
  };
  
  // Handle pattern selector
  const handleOpenPatternSelector = async (garment) => {
    try {
      const garmentDetail = garmentDetailsById[garment.garment_id] || (await getGarment(garment.garment_id)).data;

      // Fetch customer_id from order since garments don't have it directly
      const orderRes = await api.get(`/orders/${garment.order_id}`);
      const order = orderRes.data;

      setSelectedGarmentForPattern({
        ...garment,
        ...garmentDetail,
        customer_id: order.customer_id,
        garment_type: garmentDetail?.garment_type || garment.garment_type,
        is_test_garment: Boolean(
          garmentDetail?.is_test_garment
          || garment.is_test_garment
          || String(garmentDetail?.garment_type || garment.garment_type || '').toLowerCase().startsWith('test_')
          || String(garmentDetail?.garment_type || garment.garment_type || '').toLowerCase().startsWith('test ')
        ),
      });
      setPatternSelectorOpen(true);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      toast.error('Failed to load order details');
    }
  };
  
  const handleSelectPattern = async (patternCode) => {
    if (!selectedGarmentForPattern) return false;

    const garment = garments.find(g => g.garment_id === selectedGarmentForPattern.garment_id) || selectedGarmentForPattern;
    if (!garment) return false;

    setGarments(prev => prev.map(g => (
      g.garment_id === garment.garment_id ? { ...g, pattern_no: patternCode } : g
    )));

    try {
      await updateGarment(garment.garment_id, buildGarmentUpdatePayload(garment, {
        pattern_no: patternCode,
      }));
      toast.success(`Pattern ${patternCode} saved`);
      return true;
    } catch (error) {
      toast.error('Failed to save pattern no');
      setGarments(prev => prev.map(g => (
        g.garment_id === garment.garment_id ? { ...g, pattern_no: garment.pattern_no } : g
      )));
      return false;
    }
  };

  const enableVirtualization = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.location.search.includes('virtual=1');
  }, []);

  const shouldVirtualizeGarments = enableVirtualization && activeTab === 'garments' && !expandedRow && garments.length > 40;
  const garmentsVisibleStartIndex = shouldVirtualizeGarments
    ? Math.max(Math.floor(garmentsTableScrollTop / GARMENTS_VIRTUAL_ROW_HEIGHT) - GARMENTS_VIRTUAL_OVERSCAN, 0)
    : 0;
  const garmentsVisibleCount = shouldVirtualizeGarments
    ? Math.max(
        Math.ceil((garmentsTableViewportHeight || 640) / GARMENTS_VIRTUAL_ROW_HEIGHT) + GARMENTS_VIRTUAL_OVERSCAN * 2,
        40
      )
    : garments.length;
  const garmentsVisibleEndIndex = shouldVirtualizeGarments
    ? Math.min(garments.length, garmentsVisibleStartIndex + garmentsVisibleCount)
    : garments.length;
  const garmentsVisibleRows = shouldVirtualizeGarments
    ? garments.slice(garmentsVisibleStartIndex, garmentsVisibleEndIndex)
    : garments;
  const garmentsVisibleRowsMerged = garmentsVisibleRows.map((garment) => {
    const details = garmentDetailsById[garment.garment_id];
    return details ? { ...garment, ...details } : garment;
  });
  const garmentsVirtualTopPaddingPx = shouldVirtualizeGarments
    ? garmentsVisibleStartIndex * GARMENTS_VIRTUAL_ROW_HEIGHT
    : 0;
  const garmentsVirtualBottomPaddingPx = shouldVirtualizeGarments
    ? Math.max((garments.length - garmentsVisibleEndIndex) * GARMENTS_VIRTUAL_ROW_HEIGHT, 0)
    : 0;

  return (
    <div className="min-h-screen bg-[#f5f7fa] p-3" data-testid="garments-page">
      <div className="max-w-[1600px] mx-auto">
        <Dialog open={deliveryDateEditorOpen} onOpenChange={(open) => {
          setDeliveryDateEditorOpen(open);
          if (!open) {
            setDeliveryDateGarment(null);
          }
        }}>
          <DialogContent className="sm:max-w-md" data-testid="garment-date-edit-dialog">
            <DialogHeader>
              <DialogTitle>Edit Garment IDD/CDD</DialogTitle>
              <DialogDescription>
                Update garment-level delivery targets. These values are independent from planned dispatch date.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="garment-idd-input">Internal Delivery Date (IDD)</Label>
                <DateField
                  value={deliveryDateForm.internal_delivery_date || ''}
                  onChange={(next) => setDeliveryDateForm(prev => ({ ...prev, internal_delivery_date: next }))}
                  className="h-10 w-full"
                  testId="garment-date-edit-idd-input"
                  clearTestId="garment-date-edit-idd-clear-button"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="garment-cdd-input">Client Delivery Date (CDD)</Label>
                <DateField
                  value={deliveryDateForm.client_final_date || ''}
                  onChange={(next) => setDeliveryDateForm(prev => ({ ...prev, client_final_date: next }))}
                  className="h-10 w-full"
                  testId="garment-date-edit-cdd-input"
                  clearTestId="garment-date-edit-cdd-clear-button"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeliveryDateEditorOpen(false);
                  setDeliveryDateGarment(null);
                }}
                data-testid="garment-date-edit-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={saveGarmentDeliveryDates}
                disabled={deliveryDateSaving}
                data-testid="garment-date-edit-save-button"
              >
                {deliveryDateSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="garments-tabs">
          {hasProductionAccess && (
            <TabsList className="bg-white border border-slate-200 p-1 flex flex-wrap gap-1" data-testid="garments-tabs-list">
              <TabsTrigger value="garments" className="gap-2" data-testid="tab-garments">
                <Shirt className="w-4 h-4" />
                Garments
              </TabsTrigger>
              <TabsTrigger value="tailors" className="gap-2" data-testid="tab-tailors">
                <Users className="w-4 h-4" />
                Tailors
              </TabsTrigger>
              <TabsTrigger value="tailor_workload" className="gap-2" data-testid="tab-tailor-workload">
                <ListChecks className="w-4 h-4" />
                Tailor Workload
              </TabsTrigger>
              <TabsTrigger value="pattern_workload" className="gap-2" data-testid="tab-pattern-workload">
                <ClipboardList className="w-4 h-4" />
                Pattern Workload
              </TabsTrigger>
              <TabsTrigger value="task_configs" className="gap-2" data-testid="tab-task-configs">
                <Gauge className="w-4 h-4" />
                Task Configs
              </TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="garments">
            {/* Compact Header with Search */}
            <div className="mb-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-semibold text-[#2d3748]">Garment Production</h1>
                  <p className="text-xs text-[#718096]">
                    {total} garment{total !== 1 ? 's' : ''} found
                    {garments.length < total && ` (showing ${garments.length})`}
                  </p>
                </div>
                {hasProductionAccess && (
                  <div className="flex flex-wrap items-center gap-3" data-testid="production-oversight-widget">
                    <Card className="border-slate-200 shadow-sm">
                      <CardContent className="py-2 px-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Production Oversight</p>
                        <p
                          className={`text-lg font-semibold ${productionOverview.unassigned_tasks > 0 ? 'text-red-600' : 'text-slate-900'}`}
                          data-testid="unassigned-tasks-count"
                        >
                          Unassigned Tasks: {productionOverview.unassigned_tasks || 0}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {/* Search and Filters */}
              <div className="flex flex-wrap md:flex-nowrap items-start md:items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap md:flex-nowrap flex-1 min-w-0">
                  <GarmentsSearchInput
                    defaultValue={searchTerm}
                    onDebouncedChange={handleSearchTermChange}
                  />

                  <div className="w-full md:w-[240px] md:flex-none" data-testid="quick-dispatch-date-filter-container">
                    <Select
                      value={filters.dispatchDate || '__all__'}
                      onValueChange={(value) => {
                        setNoDispatchFilter(null);
                        setFilters(prev => ({
                          ...prev,
                          dispatchDate: value === '__all__' ? null : value,
                        }));
                      }}
                    >
                      <SelectTrigger className="h-9 bg-white" data-testid="quick-dispatch-date-filter-select">
                        <SelectValue placeholder="Next dispatch dates" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All dispatch dates</SelectItem>
                        {(dispatchDateOptions || []).map(option => (
                          <SelectItem key={option.date} value={option.date}>
                            {option.date} ({option.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {dispatchDateOptionsLoading && (
                      <p className="text-[11px] text-slate-400 mt-1" data-testid="quick-dispatch-date-loading">Loading next dates…</p>
                    )}
                  </div>

                  {showStoreQuickFilter && (
                    <div className="w-full md:w-[220px] md:flex-none" data-testid="quick-store-filter-container">
                      <Select
                        value={filters.store || 'all'}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, store: value }))}
                      >
                        <SelectTrigger className="h-9 bg-white" data-testid="quick-store-filter-select">
                          <SelectValue placeholder="All stores" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All stores</SelectItem>
                          {accessibleStoreOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {/* Active Filters Indicator */}
                {(Object.entries(filters).some(([key, val]) => val !== 'all' && val !== null) || debouncedSearch || noDispatchFilter || noPatternFilter) && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-2 py-1" data-testid="active-filters-badge">
                      <Filter className="w-3 h-3 mr-1" />
                      {Object.entries(filters).filter(([key, val]) => val !== 'all' && val !== null).length + (debouncedSearch ? 1 : 0) + (noDispatchFilter ? 1 : 0) + (noPatternFilter ? 1 : 0)} active
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFilters({ status: 'all', priority: 'all', garmentType: 'all', patternStatus: 'all', patternMaker: 'all', store: 'all', dispatchDate: null });
                        setNoDispatchFilter(null);
                        setNoPatternFilter(false);
                        setSearchTerm('');
                      }}
                      className="h-8 text-slate-600 hover:text-slate-900"
                      data-testid="clear-filters-button"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                )}
              </div>
            </div>

      {/* Bulk Action Card */}
      {selectedGarments.length > 0 && (
        <Card className="mb-3 border-blue-200 bg-blue-50/50" data-testid="bulk-action-bar">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-blue-900">{selectedGarments.length} garment{selectedGarments.length > 1 ? 's' : ''} selected</span>
              <Button size="sm" variant="outline" onClick={() => setBulkEditOpen(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Bulk Edit
              </Button>
              {hasProductionAccess && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleOpenAssignModal} data-testid="assign-selected-button">
                  <ListChecks className="w-4 h-4 mr-1.5" />
                  Assign ({selectedGarments.length})
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setSelectedGarments([])}>
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Garments Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : garments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Shirt className="w-16 h-16 text-[#718096]/50 mb-4" />
            <h3 className="text-lg font-medium mb-2 text-[#2d3748]">No garments found</h3>
            <p className="text-sm text-[#718096]">
              {searchTerm || Object.values(filters).some(v => v !== 'all')
                ? 'Try adjusting your search or filters' 
                : 'Garments will appear here when orders are created'}
            </p>
          </div>
        ) : (
          <div
            ref={garmentsTableScrollRef}
            onScroll={handleGarmentsTableScroll}
            className="overflow-auto max-h-[calc(100vh-140px)]"
            data-testid="garments-table-scroll-container"
          >
            <Table>
              <TableHeader className="sticky top-0 z-10 shadow-sm">
                <TableRow className="bg-[#f8fafc] border-b-2 border-[#e2e8f0] hover:bg-[#f8fafc]">
                  <TableHead className="w-10 bg-[#f8fafc]">
                    <Checkbox
                      checked={garments.length > 0 && selectedGarments.length === garments.length}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-garments"
                    />
                  </TableHead>
                  <TableHead className="w-8 bg-[#f8fafc]"></TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Order No</TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Garment No</TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Customer</TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      <span>Type</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className={`p-1 rounded hover:bg-slate-200 ${filters.garmentType !== 'all' ? 'text-blue-600' : 'text-slate-500'}`}
                            data-testid="garment-type-filter-button"
                          >
                            <Filter className="w-3 h-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56" data-testid="garment-type-filter-dropdown">
                          <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, garmentType: 'all' }))}>
                            All Types
                          </DropdownMenuItem>
                          {garmentTypeFilterOptions.map(type => (
                            <DropdownMenuItem key={type} onClick={() => setFilters(prev => ({ ...prev, garmentType: type }))}>
                              {formatGarmentTypeLabel(type)}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableHead>
                  
                  {/* Priority with Filter Icon */}
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      <span>Priority</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`h-5 w-5 p-0 rounded-sm ${
                              filters.priority !== 'all' 
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Filter className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, priority: 'all' }))}>
                            All
                          </DropdownMenuItem>
                          {priorities.map(p => (
                            <DropdownMenuItem key={p.value} onClick={() => setFilters(prev => ({ ...prev, priority: p.value }))}>
                              {p.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableHead>
                  
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Pattern No</TableHead>
                  
                  {/* Pattern Status with Filter Icon */}
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      <span>Pattern Status</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`h-5 w-5 p-0 rounded-sm ${
                              filters.patternStatus !== 'all' 
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Filter className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, patternStatus: 'all' }))}>
                            All
                          </DropdownMenuItem>
                          {patternStatuses.map(s => (
                            <DropdownMenuItem key={s.value} onClick={() => setFilters(prev => ({ ...prev, patternStatus: s.value }))}>
                              {s.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableHead>
                  
                  {/* Pattern Maker with Filter Icon */}
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      <span>Pattern Maker</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`h-5 w-5 p-0 rounded-sm ${
                              filters.patternMaker !== 'all' 
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Filter className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, patternMaker: 'all' }))}>
                            All
                          </DropdownMenuItem>
                          {patternMakers.map(pm => (
                            <DropdownMenuItem key={pm.value} onClick={() => setFilters(prev => ({ ...prev, patternMaker: pm.value }))}>
                              {pm.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableHead>
                  
                  {/* Status with Filter Icon */}
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      <span>Status</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`h-5 w-5 p-0 rounded-sm ${
                              filters.status !== 'all' 
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Filter className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, status: 'all' }))}>
                            All
                          </DropdownMenuItem>
                          {garmentStatuses.map(s => (
                            <DropdownMenuItem key={s.value} onClick={() => setFilters(prev => ({ ...prev, status: s.value }))}>
                              {s.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableHead>
                  
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                    <div className="flex items-center gap-1">
                      Dispatch Date
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`h-5 w-5 p-0 rounded-sm ${
                              filters.dispatchDate || noDispatchFilter
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Filter className="w-3 h-3" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={filters.dispatchDate ? new Date(filters.dispatchDate) : undefined}
                            onSelect={(date) => {
                              setNoDispatchFilter(null);
                              setFilters(prev => ({
                                ...prev,
                                dispatchDate: date ? format(date, 'yyyy-MM-dd') : null
                              }));
                            }}
                            initialFocus
                          />
                          <div className="p-2 border-t space-y-1">
                            <Button
                              variant={noDispatchFilter ? "default" : "ghost"}
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => {
                                setFilters(prev => ({ ...prev, dispatchDate: null }));
                                setNoDispatchFilter(noDispatchFilter ? null : 'no_dispatch');
                              }}
                            >
                              {noDispatchFilter ? '✓ Showing Blank Only' : 'Show Blank (No Date)'}
                            </Button>
                            {(filters.dispatchDate || noDispatchFilter) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full text-xs text-slate-500"
                                onClick={() => {
                                  setFilters(prev => ({ ...prev, dispatchDate: null }));
                                  setNoDispatchFilter(null);
                                }}
                              >
                                Clear Filter
                              </Button>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Images</TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">PDF</TableHead>
                  <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Plan</TableHead>
                </TableRow>
              </TableHeader>
                <TableBody>
                  {shouldVirtualizeGarments && garmentsVirtualTopPaddingPx > 0 && (
                    <TableRow data-testid="garments-virtual-top-spacer">
                      <TableCell colSpan={15} className="p-0 border-0" style={{ height: `${garmentsVirtualTopPaddingPx}px` }} />
                    </TableRow>
                  )}

                  {garmentsVisibleRowsMerged.map((garment) => {
                    const statusConfig = getStatusConfig(garment.garment_status, 'garment');
                    const patternStatusConfig = getStatusConfig(garment.pattern_status || 'not_ready', 'pattern');
                    const priorityConfig = getPriorityConfig(garment.priority);
                    const garmentDetailsLoaded = Boolean(garmentDetailsById[garment.garment_id]);
                    
                    return (
                      <Fragment key={garment.garment_id}>
                        {/* Main Garment Row - Clickable to expand */}
                        <TableRow 
                          className="border-border hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={(e) => {
                            // Don't toggle if clicking on interactive elements
                            if (!e.target.closest('input, select, button, a, [role="checkbox"]')) {
                              toggleRowExpansion(garment.garment_id);
                            }
                          }}
                        >
                          {/* Selection Checkbox */}
                          <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedGarments.includes(garment.garment_id)}
                              onCheckedChange={(checked) => handleSelectGarment(garment.garment_id, checked)}
                              data-testid={`select-garment-${garment.garment_id}`}
                            />
                          </TableCell>
                          {/* Chevron Icon */}
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpansion(garment.garment_id);
                              }}
                            >
                              {expandedRow === garment.garment_id ? (
                                <ChevronDown className="w-4 h-4 text-slate-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </Button>
                          </TableCell>
                          
                          {/* Order Number with Copy */}
                          <TableCell>
                            <div className="flex flex-col items-start gap-0.5">
                              <span className="font-mono text-xs text-muted-foreground">
                                {garment.order_number}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 opacity-60 hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(garment.order_number);
                                  toast.success('Order number copied!');
                                }}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          
                          {/* Garment Number (Just the suffix like J1, TJ1) with Dates */}
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className="font-mono text-xs font-semibold bg-primary/10 border-primary/30 text-primary w-fit">
                                {extractGarmentSuffix(garment.garment_no, garment.order_number)}
                              </Badge>
                              <div className="flex flex-col">
                                {garment.is_test_garment ? (
                                  <>
                                    <span className="text-[10px] text-muted-foreground leading-tight">
                                      TFD: {garment.test_fit_date ? formatDateWord(garment.test_fit_date, 'Not set') : 'Not set'}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground leading-tight">
                                      IDD: {garment.internal_delivery_date ? formatDateWord(garment.internal_delivery_date, 'Not set') : 'Not set'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-[10px] text-muted-foreground leading-tight">
                                      IDD: {garment.internal_delivery_date ? formatDateWord(garment.internal_delivery_date, 'Not set') : 'Not set'}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground leading-tight">
                                      CDD: {garment.client_final_date ? formatDateWord(garment.client_final_date, 'Not set') : 'Not set'}
                                    </span>
                                  </>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 mt-0.5 text-slate-500 hover:text-slate-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeliveryDateEditor(garment);
                                }}
                                data-testid={`garment-date-edit-button-${garment.garment_id}`}
                                title="Edit garment IDD/CDD"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          
                          {/* Customer - Show customer_no */}
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{garment.customer_name || '—'}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {formatCustomerNo(garment.customer_no, garment.customer_name)}
                              </p>
                            </div>
                          </TableCell>
                          
                          {/* Type */}
                          <TableCell>
                            <span data-testid={`garment-type-${garment.garment_id}`}>{formatGarmentTypeLabel(garment.garment_type)}</span>
                          </TableCell>
                          
                          {/* Priority */}
                          <TableCell>
                            <Select
                              value={garment.priority}
                              onValueChange={(v) => handleFieldUpdate(garment.garment_id, 'priority', v)}
                            >
                              <SelectTrigger className="h-8 w-24 border-0 bg-transparent hover:bg-slate-100">
                                <Badge variant="outline" className={`${priorityConfig.color} border-0`}>
                                  {priorityConfig.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {priorities.map(p => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          
                          {/* Pattern No */}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input
                                value={garment.pattern_no || ''}
                                placeholder="Select..."
                                className="h-8 w-20 text-xs bg-transparent border-dashed"
                                onChange={(e) => {
                                  // Update local state immediately for responsive UI
                                  setGarments(prev => prev.map(g => 
                                    g.garment_id === garment.garment_id ? { ...g, pattern_no: e.target.value } : g
                                  ));
                                }}
                                onBlur={(e) => {
                                  // Save to backend only when field is blurred
                                  debouncedFieldUpdate(garment.garment_id, 'pattern_no', e.target.value, garment);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPatternSelector(garment);
                                }}
                                title="Select from available patterns"
                              >
                                <Info className="w-4 h-4 text-blue-500" />
                              </Button>
                            </div>
                          </TableCell>
                          
                          {/* Pattern Status */}
                          <TableCell>
                            <Select
                              value={garment.pattern_status === 'in_progress' ? 'for_approval' : (garment.pattern_status || 'not_ready')}
                              onValueChange={(v) => handleFieldUpdate(garment.garment_id, 'pattern_status', v)}
                            >
                              <SelectTrigger className="h-8 w-28 border-0 bg-transparent hover:bg-slate-100">
                                <Badge variant="outline" className={`${patternStatusConfig.color} text-xs`}>
                                  {patternStatusConfig.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {patternStatuses.map(s => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          
                          {/* Pattern Maker */}
                          <TableCell>
                            <Select
                              value={garment.pattern_maker || '__none__'}
                              onValueChange={(v) => handleFieldUpdate(garment.garment_id, 'pattern_maker', v)}
                            >
                              <SelectTrigger className="h-8 w-32 border-0 bg-transparent hover:bg-slate-100 text-xs">
                                <SelectValue placeholder="Not assigned" />
                              </SelectTrigger>
                              <SelectContent>
                                {patternMakers.map(m => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          
                          {/* Garment Status */}
                          <TableCell>
                            <Select
                              value={garment.garment_status}
                              onValueChange={(v) => handleFieldUpdate(garment.garment_id, 'garment_status', v)}
                            >
                              <SelectTrigger className="h-8 w-32 border-0 bg-transparent hover:bg-slate-100">
                                <Badge variant="outline" className={`${statusConfig.color} text-xs`}>
                                  {statusConfig.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {garmentStatuses.map(s => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          
                          {/* Dispatch Date */}
                          <TableCell>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  className="h-8 px-2 text-xs font-normal text-slate-700 hover:bg-orange-100 hover:text-orange-700"
                                >
                                  <CalendarIcon className="w-3 h-3 mr-1" />
                                  {garment.planned_dispatch_date 
                                    ? formatDateWord(garment.planned_dispatch_date)
                                    : 'Not set'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={garment.planned_dispatch_date ? new Date(garment.planned_dispatch_date) : undefined}
                                  onSelect={(date) => {
                                    if (date) {
                                      handleFieldUpdate(
                                        garment.garment_id, 
                                        'planned_dispatch_date', 
                                        format(date, 'yyyy-MM-dd')
                                      );
                                    }
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </TableCell>
                          
                          {/* Images */}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs relative"
                              onClick={() => handleAddImages(garment)}
                              data-testid={`garment-images-button-${garment.garment_id}`}
                            >
                              <ImageIcon className="w-4 h-4" />
                              {garment.image_count > 0 && (
                                <Badge variant="secondary" className="absolute -top-1 -right-1 h-5 px-1.5 text-[10px]">
                                  {garment.image_count}
                                </Badge>
                              )}
                            </Button>
                          </TableCell>
                          
                          {/* PDF */}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleGenerateOrderSheet(garment)}
                              data-testid={`garment-order-sheet-button-${garment.garment_id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </TableCell>

                          {/* Plan status */}
                          <TableCell onClick={e => e.stopPropagation()}>
                            {(() => {
                              const garmentTasks = plannedTasksByGarment[garment.garment_id] || [];
                              const isPlanned = garmentTasks.length > 0;
                              return (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
                                      title={isPlanned ? 'Planned tasks exist' : 'No planned tasks'}
                                      data-testid={`garment-plan-status-trigger-${garment.garment_id}`}
                                    >
                                      <span
                                        className={`w-3 h-3 rounded-full border ${isPlanned ? 'bg-emerald-500 border-emerald-600' : 'bg-red-500 border-red-600'}`}
                                        data-testid={`garment-plan-status-dot-${garment.garment_id}`}
                                      />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80" align="end" data-testid={`garment-plan-status-popover-${garment.garment_id}`}>
                                    <div className="space-y-2">
                                      <div>
                                        <p className="text-xs font-semibold text-slate-800">{garment.garment_no}</p>
                                        <p className="text-[11px] text-slate-500">
                                          {isPlanned ? `${garmentTasks.length} planned task${garmentTasks.length !== 1 ? 's' : ''}` : 'Unplanned'}
                                        </p>
                                      </div>
                                      {isPlanned ? (
                                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                          {garmentTasks.slice(0, 8).map(task => (
                                            <div key={task.task_id} className="rounded border border-slate-200 px-2 py-1.5" data-testid={`garment-plan-task-${garment.garment_id}-${task.task_id}`}>
                                              <p className="text-xs text-slate-700">
                                                {task.title?.split(' - ')[0] || task.task_key || 'Task'}
                                              </p>
                                              <p className="text-[11px] text-slate-500">
                                                Planned to: {(task.assigned_to || []).join(', ') || 'Unassigned'}
                                              </p>
                                            </div>
                                          ))}
                                          {garmentTasks.length > 8 && (
                                            <p className="text-[11px] text-slate-400">+{garmentTasks.length - 8} more task(s)</p>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-red-600">No production tasks planned yet.</p>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable Detail Row */}
                        {expandedRow === garment.garment_id && (
                          <TableRow className="bg-slate-50 border-t-0">
                            <TableCell colSpan={15} className="py-4 px-8">
                              {garmentDetailsLoaded ? (
                                <GarmentExpandedRowContent
                                  garment={garment}
                                  onViewCustomizations={handleViewCustomizations}
                                  onUpdateGarmentField={handleFieldUpdate}
                                  getFabricStatusConfig={getFabricStatusConfig}
                                />
                              ) : (
                                <div className="py-8 flex items-center justify-center text-sm text-slate-500" data-testid={`garment-detail-loading-${garment.garment_id}`}>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Loading garment details...
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}

                  {shouldVirtualizeGarments && garmentsVirtualBottomPaddingPx > 0 && (
                    <TableRow data-testid="garments-virtual-bottom-spacer">
                      <TableCell colSpan={15} className="p-0 border-0" style={{ height: `${garmentsVirtualBottomPaddingPx}px` }} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="p-4 border-t border-[#e2e8f0] flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="min-w-[200px]"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      `Load More (${garments.length} of ${total})`
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
      </div>
      
      {/* Garment Images Modal */}
      <GarmentImagesModal
        garment={selectedGarmentForImages}
        isOpen={imageManagerOpen}
        onClose={() => {
          setImageManagerOpen(false);
          setSelectedGarmentForImages(null);
        }}
        onUpdate={handleImagesUpdated}
      />
      
      {/* Pattern Selector Modal */}
      {selectedGarmentForPattern && (
        <PatternSelectorModal
          isOpen={patternSelectorOpen}
          onClose={() => {
            setPatternSelectorOpen(false);
            setSelectedGarmentForPattern(null);
          }}
          customerId={selectedGarmentForPattern.customer_id}
          garmentType={selectedGarmentForPattern.garment_type}
          garment={selectedGarmentForPattern}
          sourceCustomizations={selectedGarmentForPattern.customizations || []}
          availableStores={stores.map((store) => store.name || store.store_id).filter(Boolean)}
          onSelectPattern={handleSelectPattern}
        />
      )}
      
      {/* Customizations Dialog */}
      <Dialog open={customizationsOpen} onOpenChange={setCustomizationsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Design Customizations</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedGarmentForCustomizations?.garment_no} - {selectedGarmentForCustomizations?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedGarmentForCustomizations?.customizations && selectedGarmentForCustomizations.customizations.length > 0 ? (
            <div className="space-y-4 mt-4">
              {/* Sort and group customizations by category using snapshot schema */}
              {(() => {
                const customizations = selectedGarmentForCustomizations.customizations;
                const schemaToUse = selectedGarmentForCustomizations.snapshot_customization_schema?.categories;
                
                // Sort customizations using the same logic as LineItemRow
                const sortedCustomizations = sortCustomizations(customizations, schemaToUse);
                
                // Group by category while preserving category metadata
                const grouped = sortedCustomizations.reduce((acc, c) => {
                  const categoryName = c.category_name || 'General';
                  const category = schemaToUse?.find(cat => cat.name === categoryName);
                  
                  if (!acc[categoryName]) {
                    acc[categoryName] = {
                      name: categoryName,
                      display_order: category?.display_order ?? 999,
                      options: []
                    };
                  }
                  acc[categoryName].options.push(c);
                  return acc;
                }, {});
                
                // Convert to array and sort by display_order
                const groupedCustomizations = Object.values(grouped).sort((a, b) => a.display_order - b.display_order);
                
                return groupedCustomizations.map((categoryGroup) => (
                  <div key={categoryGroup.name} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <h4 className="font-semibold text-sm text-slate-900 mb-3 uppercase tracking-wide">{categoryGroup.name}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      {categoryGroup.options.map((option, optIdx) => (
                        <div key={optIdx} className="flex items-baseline gap-2">
                          <span className="text-sm text-slate-600 min-w-[140px]">{option.option_name}:</span>
                          <span className="text-sm font-medium text-slate-900">
                            {option.selected_value}
                            {option.price_impact > 0 && (
                              <span className="text-orange-600 ml-1 text-xs">(+${option.price_impact})</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              <p className="text-sm">No customizations available for this garment</p>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setCustomizationsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* No Pattern Warning Dialog */}
      <AlertDialog open={noPatternWarningOpen} onOpenChange={setNoPatternWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="text-amber-600">⚠️</span> No Pattern Assigned
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This garment <span className="font-semibold">{garmentPendingOrderSheet?.garment_no}</span> does not have a pattern assigned.
              </p>
              <p>
                The order sheet will <span className="font-semibold">only show body measurements</span> without pattern specifications, which may be incomplete for production.
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3 mt-2">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Recommendation:</strong> Cancel and assign a pattern first for a complete order sheet with both body and garment measurements.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setNoPatternWarningOpen(false);
              setGarmentPendingOrderSheet(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleProceedWithoutPattern}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Proceed Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={(open) => { if (!open) setBulkEditOpen(false); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedGarments.length} Garment{selectedGarments.length > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Only fields you change will be updated. Leave fields blank to skip them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Priority</Label>
                <Select value={bulkValues.priority} onValueChange={(v) => setBulkValues(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={bulkValues.garment_status} onValueChange={(v) => setBulkValues(prev => ({ ...prev, garment_status: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    {garmentStatuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Pattern Maker</Label>
                <Select value={bulkValues.pattern_maker} onValueChange={(v) => setBulkValues(prev => ({ ...prev, pattern_maker: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassign</SelectItem>
                    {patternMakers.map(pm => (
                      <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Planned Dispatch Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-9 justify-start text-sm font-normal">
                      <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                      {bulkValues.planned_dispatch_date ? formatDateWord(bulkValues.planned_dispatch_date, 'No change') : 'No change'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={bulkValues.planned_dispatch_date ? new Date(bulkValues.planned_dispatch_date) : undefined}
                      onSelect={(date) => setBulkValues(prev => ({ ...prev, planned_dispatch_date: date ? format(date, 'yyyy-MM-dd') : null }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                const updates = Object.entries(bulkValues).filter(([_, v]) => v && v !== '');
                if (updates.length === 0) { toast.error('No changes selected'); return; }
                for (const [field, value] of updates) {
                  await handleBulkUpdate(field, value);
                }
                setBulkEditOpen(false);
                setBulkValues({ priority: '', garment_status: '', pattern_maker: '', planned_dispatch_date: null });
                setSelectedGarments([]);
              }}
            >
              Apply to {selectedGarments.length} Garment{selectedGarments.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
    {hasProductionAccess && (
      <>
        <TabsContent value="tailors">
          <Card className="border-slate-200" data-testid="tailors-tab-content">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Tailors</CardTitle>
                <p className="text-xs text-slate-500">Manage teams, primary tasks, and capabilities.</p>
              </div>
              <Button onClick={() => handleOpenTailorDialog()} data-testid="add-tailor-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Tailor
              </Button>
            </CardHeader>
            <CardContent>
              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="tailors-filters">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600 font-medium">Filter:</span>
                </div>
                <Select value={tailorFilterTeam} onValueChange={setTailorFilterTeam} data-testid="tailor-filter-team">
                  <SelectTrigger className="h-8 w-52 text-sm" data-testid="tailor-filter-team-trigger">
                    <SelectValue placeholder="All Teams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {tailorTeamOptions.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={tailorFilterStatus} onValueChange={setTailorFilterStatus} data-testid="tailor-filter-status">
                  <SelectTrigger className="h-8 w-36 text-sm" data-testid="tailor-filter-status-trigger">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400 ml-1">
                  {displayedTailors.length} of {tailors.length} tailors
                </span>
              </div>

              {/* Bulk action bar */}
              {selectedTailorIds.size > 0 && (
                <div className="flex items-center gap-3 flex-wrap mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid="tailor-bulk-action-bar">
                  <span className="text-sm font-medium text-blue-900">{selectedTailorIds.size} tailor{selectedTailorIds.size > 1 ? 's' : ''} selected</span>
                  <Button size="sm" variant="outline" onClick={() => { setTailorBulkField('primary_garment_types'); setTailorBulkAction('add'); setTailorBulkValues([]); setTailorBulkEditOpen(true); }} data-testid="tailor-bulk-edit-button">
                    <Edit className="w-4 h-4 mr-1" />
                    Bulk Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setSelectedTailorIds(new Set())} data-testid="tailor-bulk-clear-button">
                    Clear Selection
                  </Button>
                </div>
              )}

              {tailorsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : displayedTailors.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="tailors-empty-state">
                  {tailors.length === 0 ? 'No tailors added yet. Add your first tailor to begin assignments.' : 'No tailors match the current filters.'}
                </div>
              ) : (
                <Table data-testid="tailors-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <Checkbox
                          checked={displayedTailors.length > 0 && selectedTailorIds.size === displayedTailors.length}
                          onCheckedChange={toggleSelectAllTailors}
                          data-testid="select-all-tailors"
                        />
                      </TableHead>
                      <TableHead>
                        <button
                          className="flex items-center gap-1 hover:text-slate-900 font-semibold text-slate-700 text-xs"
                          onClick={() => toggleTailorSort('name')}
                          data-testid="sort-by-name"
                        >
                          Name
                          {tailorSortBy === 'name' ? (
                            tailorSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          className="flex items-center gap-1 hover:text-slate-900 font-semibold text-slate-700 text-xs"
                          onClick={() => toggleTailorSort('employee_id')}
                          data-testid="sort-by-employee-id"
                        >
                          Employee ID
                          {tailorSortBy === 'employee_id' ? (
                            tailorSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Primary Task</TableHead>
                      <TableHead>Capabilities</TableHead>
                      <TableHead>Primary Garments</TableHead>
                      <TableHead>Secondary Garments</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedTailors.map(tailor => (
                      <TableRow key={tailor.tailor_id} data-testid={`tailor-row-${tailor.tailor_id}`} className={selectedTailorIds.has(tailor.tailor_id) ? 'bg-blue-50' : ''}>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedTailorIds.has(tailor.tailor_id)}
                            onCheckedChange={() => toggleTailorSelection(tailor.tailor_id)}
                            data-testid={`select-tailor-${tailor.tailor_id}`}
                          />
                        </TableCell>
                        <TableCell data-testid={`tailor-name-${tailor.tailor_id}`}>{tailor.name}</TableCell>
                        <TableCell data-testid={`tailor-employee-${tailor.tailor_id}`}>{tailor.employee_id || '-'}</TableCell>
                        <TableCell data-testid={`tailor-team-${tailor.tailor_id}`}>{tailor.team || '-'}</TableCell>
                        <TableCell data-testid={`tailor-primary-${tailor.tailor_id}`}>{getCapabilityLabel(tailor.primary_task) || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {tailor.capabilities?.length ? (
                              tailor.capabilities.map(cap => (
                                <Badge key={cap} variant="outline" className="text-xs" data-testid={`tailor-capability-${tailor.tailor_id}-${cap}`}>
                                  {getCapabilityLabel(cap)}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400">None</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {tailor.primary_garment_types?.length ? (
                              tailor.primary_garment_types.map(g => (
                                <Badge key={g} variant="outline" className="text-xs border-blue-200 text-blue-700">{formatGarmentTypeLabel(g)}</Badge>
                              ))
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {tailor.secondary_garment_types?.length ? (
                              tailor.secondary_garment_types.map(g => (
                                <Badge key={g} variant="outline" className="text-xs border-purple-200 text-purple-700">{formatGarmentTypeLabel(g)}</Badge>
                              ))
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={tailor.is_active ? 'border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-500'} data-testid={`tailor-status-${tailor.tailor_id}`}>
                            {tailor.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenTailorDialog(tailor)} data-testid={`edit-tailor-${tailor.tailor_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTailor(tailor.tailor_id)} data-testid={`delete-tailor-${tailor.tailor_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tailor_workload">
          <Card className="border-slate-200" data-testid="tailor-workload-tab">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-lg">Tailor Workload</CardTitle>
                <p className="text-xs text-slate-500">Track production tasks by tailor, team, or order. Click any cell to edit inline.</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Subtab toggle */}
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                  <button
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${workloadSubTab === 'tailors' ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setWorkloadSubTab('tailors')}
                    data-testid="workload-subtab-task"
                  >
                    Tailors
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${workloadSubTab === 'all_tasks' ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setWorkloadSubTab('all_tasks')}
                    data-testid="workload-subtab-all-tasks"
                  >
                    All Tasks
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${workloadSubTab === 'calendar' ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setWorkloadSubTab('calendar')}
                    data-testid="workload-subtab-workload"
                  >
                    Calendar
                  </button>
                </div>
                {selectedTaskIds.size > 0 && (workloadSubTab === 'tailors' || workloadSubTab === 'all_tasks') && (
                  <div className="flex items-center gap-2" data-testid="bulk-selection-actions">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setSelectedTaskIds(new Set())}
                      title="Deselect all selected tasks"
                      aria-label="Deselect all selected tasks"
                      data-testid="deselect-all-tasks-button"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={handleBulkDeleteTasks} disabled={bulkDeleting} data-testid="bulk-delete-tasks-button">
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete {selectedTaskIds.size} selected
                    </Button>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => fetchProductionTasks({ includeCompleted: includeCompletedTasks })} data-testid="refresh-tailor-workload-button">
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="default" size="sm" onClick={openWorkloadAdHocModal} data-testid="open-ad-hoc-task-modal-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Ad-hoc Task
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* ── Calendar View — schedule from TODAY ──────────────────── */}
              {workloadSubTab === 'calendar' && (() => {
                const TASK_COLORS = {
                  cutting: { bg: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-400' },
                  inner_sewing: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-400' },
                  outer_sewing: { bg: 'bg-violet-100 text-violet-800 border-violet-200', dot: 'bg-violet-400' },
                  sewing: { bg: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-400' },
                  pattern: { bg: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
                };
                const defaultColor = { bg: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-300' };

                const today = new Date(); today.setHours(0, 0, 0, 0);
                const fmtDate = d => d.toISOString().slice(0, 10);
                const isWeekend = d => d.getDay() === 0 || d.getDay() === 6;
                const isToday = d => fmtDate(d) === fmtDate(today);
                const days = Array.from({ length: 14 }, (_, i) => {
                  const d = new Date(today); d.setDate(d.getDate() + i); return d;
                });
                const dayKeys = days.map(fmtDate);

                // Unique teams for filter dropdown
                const calendarTeams = [...new Set(tailors.map(t => t.team).filter(Boolean))].sort();

                // Tailors filtered by team
                const calFilteredTailors = tailors.filter(t => {
                  if (calendarTeamFilter !== 'all' && t.team !== calendarTeamFilter) return false;
                  if (calendarTailorFilter !== 'all' && t.tailor_id !== calendarTailorFilter) return false;
                  return true;
                });

                // Active tailors (have at least one non-done task)
                const activeTailors = calFilteredTailors.filter(t =>
                  productionTasks.some(pt =>
                    pt.assigned_tailor_id === t.tailor_id &&
                    !['completed', 'cancelled'].includes(pt.status)
                  )
                );

                // Greedy schedule from today: fill each tailor's shift capacity day by day
                const buildSchedule = (tailorId, shiftMins) => {
                  const myTasks = productionTasks
                    .filter(pt =>
                      pt.assigned_tailor_id === tailorId &&
                      !['completed', 'cancelled'].includes(pt.status)
                    )
                    .sort((a, b) => {
                      const qa = a.queue_position ?? 9999, qb = b.queue_position ?? 9999;
                      return qa !== qb ? qa - qb : (a.due_date || '').localeCompare(b.due_date || '');
                    });

                  const grid = {}; // dateKey → [{task, isOverflow}]
                  let dayIdx = 0;
                  let usedToday = 0;

                  for (const task of myTasks) {
                    const mins = task.estimated_minutes || 60;
                    // Advance day if this task doesn't fit and we've already started filling
                    while (dayIdx < 14 && (usedToday + mins > shiftMins) && usedToday > 0) {
                      dayIdx++;
                      usedToday = 0;
                    }
                    if (dayIdx >= 14) break;
                    const dateKey = dayKeys[dayIdx];
                    if (!grid[dateKey]) grid[dateKey] = [];
                    const isOverflow = usedToday + mins > shiftMins;
                    grid[dateKey].push({ task, isOverflow });
                    usedToday += mins;
                    if (usedToday >= shiftMins) { dayIdx++; usedToday = 0; }
                  }
                  return grid;
                };

                if (activeTailors.length === 0) return (
                  <div className="space-y-3" data-testid="workload-calendar-view">
                    {/* Filters even when empty */}
                    <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
                      <Select value={calendarTeamFilter} onValueChange={v => { setCalendarTeamFilter(v); setCalendarTailorFilter('all'); }}>
                        <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="All Teams" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Teams</SelectItem>
                          {calendarTeams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={calendarTailorFilter} onValueChange={setCalendarTailorFilter}>
                        <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="All Tailors" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tailors</SelectItem>
                          {calFilteredTailors.map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="py-10 text-center text-sm text-slate-400">No active tasks to schedule. Assign garments to see the calendar.</div>
                  </div>
                );

                return (
                  <div className="space-y-3" data-testid="workload-calendar-view">
                    {/* Filters + Legend */}
                    <div className="flex flex-wrap items-center gap-3 pb-2 border-b border-slate-100">
                      <Select value={calendarTeamFilter} onValueChange={v => { setCalendarTeamFilter(v); setCalendarTailorFilter('all'); }}>
                        <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="All Teams" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Teams</SelectItem>
                          {calendarTeams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={calendarTailorFilter} onValueChange={setCalendarTailorFilter}>
                        <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="All Tailors" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tailors</SelectItem>
                          {calFilteredTailors.map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex flex-wrap items-center gap-3 ml-2">
                        {Object.entries(TASK_COLORS).map(([type, { dot }]) => (
                          <span key={type} className="flex items-center gap-1.5 text-xs text-slate-500">
                            <span className={`w-2 h-2 rounded-full ${dot}`} />
                            {type.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                      <span className="ml-auto text-xs text-slate-400">{activeTailors.length} tailors · scheduled from today</span>
                    </div>

                    {/* Calendar grid */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-xs min-w-[900px]">
                        <thead>
                          <tr>
                            <th className="w-28 text-left pr-3 pb-2 text-slate-500 font-medium sticky left-0 bg-white z-10">Tailor</th>
                            {days.map(d => (
                              <th
                                key={fmtDate(d)}
                                className={`text-center pb-2 px-1 font-medium min-w-[72px] ${isToday(d) ? 'text-blue-600' : isWeekend(d) ? 'text-slate-300' : 'text-slate-500'}`}
                              >
                                <div>{d.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                                <div className={`text-xs ${isToday(d) ? 'bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center mx-auto' : ''}`}>
                                  {d.getDate()}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeTailors.map(tailor => {
                            const shiftMins = tailor.shift_minutes || 510;
                            const schedule = buildSchedule(tailor.tailor_id, shiftMins);
                            return (
                              <tr key={tailor.tailor_id} className="border-t border-slate-100 group">
                                <td className="pr-3 py-2 sticky left-0 bg-white z-10 group-hover:bg-slate-50">
                                  <p className="font-medium text-slate-800 truncate">{tailor.name}</p>
                                  <p className="text-slate-400 text-[10px]">{tailor.team || '—'}</p>
                                </td>
                                {days.map(d => {
                                  const dateKey = fmtDate(d);
                                  const dayEntries = schedule[dateKey] || [];
                                  const totalMins = dayEntries.reduce((s, e) => s + (e.task.estimated_minutes || 0), 0);
                                  const utilPct = Math.min((totalMins / shiftMins) * 100, 100);
                                  const isOver = totalMins > shiftMins;
                                  return (
                                    <td key={dateKey} className={`px-1 py-1.5 align-top ${isWeekend(d) ? 'bg-slate-50/60' : ''} ${isToday(d) ? 'bg-blue-50/40' : ''}`}>
                                      {dayEntries.length > 0 ? (
                                        <div className="space-y-1">
                                          {dayEntries.map(({ task: pt, isOverflow }) => {
                                            const color = TASK_COLORS[pt.task_type] || defaultColor;
                                            const dispatchDate = (pt.due_date || '').slice(0, 10);
                                            return (
                                              <div
                                                key={pt.task_id}
                                                title={`${pt.title}\n${pt.task_type} · ${pt.estimated_minutes}min · ${pt.status}\nDispatch: ${dispatchDate}`}
                                                className={`rounded border px-1.5 py-0.5 truncate cursor-default ${color.bg} ${isOverflow ? 'opacity-70 border-dashed' : ''}`}
                                              >
                                                {pt.garment_no || pt.title?.split(' - ')[1] || pt.task_type}
                                                {dispatchDate && <span className="ml-1 text-[9px] opacity-50">{dispatchDate.slice(5)}</span>}
                                              </div>
                                            );
                                          })}
                                          <div className="mt-1">
                                            <div className="h-1 rounded-full bg-slate-200 overflow-hidden">
                                              <div
                                                className={`h-full rounded-full ${isOver ? 'bg-red-400' : totalMins > shiftMins * 0.8 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                                                style={{ width: `${utilPct}%` }}
                                              />
                                            </div>
                                            <p className={`text-[10px] text-right mt-0.5 tabular-nums ${isOver ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
                                              {(totalMins / 60).toFixed(1)}h{isOver ? ' ⚠' : ''}
                                            </p>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="h-8" />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ── Tailors View (accordion) ──────────────────────────────── */}
              {(workloadSubTab === 'tailors' || workloadSubTab === 'all_tasks') && (<>
              {/* Filters row */}
              <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="prod-task-filters">
                <div className="relative flex-1 min-w-[260px]">
                  <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
                  <input
                    className="w-full pl-8 pr-9 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-400"
                    placeholder="Search by order, garment, or customer name..."
                    value={taskSearch}
                    onChange={e => setTaskSearch(e.target.value)}
                    data-testid="prod-task-search"
                  />
                  {taskSearch && (
                    <button
                      type="button"
                      className="absolute right-2 top-1.5 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      onClick={() => setTaskSearch('')}
                      aria-label="Clear task search"
                      title="Clear search"
                      data-testid="clear-prod-task-search-button"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <Select value={taskTeamFilter} onValueChange={v => { setTaskTeamFilter(v); setTaskTailorFilter('all'); }}>
                  <SelectTrigger className="h-8 w-52 text-sm"><SelectValue placeholder="All Teams" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {tailorTeamOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={taskTailorFilter} onValueChange={setTaskTailorFilter}>
                  <SelectTrigger className="h-8 w-44 text-sm"><SelectValue placeholder="All Tailors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tailors</SelectItem>
                    {taskFilterTailors.map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
                  <SelectTrigger className="h-8 w-36 text-sm"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {PROD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="h-8 px-2.5 rounded-md border border-slate-200 bg-white flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="include-completed-filter">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 cursor-pointer"
                    checked={includeCompletedTasks}
                    onChange={e => setIncludeCompletedTasks(e.target.checked)}
                    data-testid="include-completed-checkbox"
                  />
                  Include completed
                </label>
                <span className="text-xs text-slate-400">{filteredProductionTasks.length} of {productionTasks.length} tasks</span>
              </div>

              {/* Expand / Collapse all */}
              {workloadSubTab === 'tailors' && filteredProductionTasks.length > 0 && !prodTasksLoading && (
                <div className="flex items-center gap-2 mb-3">
                  <Button size="sm" variant="ghost" className="text-xs text-slate-500 h-7"
                    onClick={() => setExpandedTailorGroups(new Set([
                      ...tasksByTailor.groups.map(g => g.tailor?.tailor_id || 'unknown'),
                      ...(tasksByTailor.unassigned.length > 0 ? ['__unassigned__'] : [])
                    ]))}>
                    Expand All
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs text-slate-500 h-7"
                    onClick={() => setExpandedTailorGroups(new Set())}>
                    Collapse All
                  </Button>
                  <span className="text-xs text-slate-400 ml-1">
                    {tasksByTailor.groups.length} tailor{tasksByTailor.groups.length !== 1 ? 's' : ''}{tasksByTailor.unassigned.length > 0 ? ` · ${tasksByTailor.unassigned.length} unassigned` : ''}
                  </span>
                </div>
              )}
              {prodTasksLoading ? (
                <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : workloadSubTab === 'all_tasks' ? null : filteredProductionTasks.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center" data-testid="prod-tasks-empty">
                  {productionTasks.length === 0 ? 'No production tasks yet. Run Auto-Assign to generate tasks.' : 'No tasks match the current filters.'}
                </div>
              ) : (
                <div className="space-y-3" data-testid="prod-tasks-grouped">
                  {/* Tailor groups */}
                  {tasksByTailor.groups.map(({ tailor, tasks }) => {
                    const tailorId = tailor?.tailor_id || 'unknown';
                    const isOpen = expandedTailorGroups.has(tailorId);
                    const blockedCount = tasks.filter(isTaskBlocked).length;
                    const totalMins = tasks.reduce((s, t) => s + (t.estimated_minutes || 0), 0);
                    return (
                      <div key={tailorId} className="border border-slate-200 rounded-lg overflow-hidden">
                        {/* Group header */}
                        <button
                          className="w-full flex items-center gap-3 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                          onClick={() => setExpandedTailorGroups(prev => {
                            const next = new Set(prev);
                            next.has(tailorId) ? next.delete(tailorId) : next.add(tailorId);
                            return next;
                          })}
                          data-testid={`tailor-group-${tailorId}`}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                          <span className="font-semibold text-sm text-slate-800">{tailor?.name || 'Unknown Tailor'}</span>
                          <span className="text-xs text-slate-400">{tailor?.team || ''}</span>
                          <div className="flex items-center gap-2 ml-auto">
                            {blockedCount > 0 && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 border">{blockedCount} blocked</Badge>
                            )}
                            <Badge variant="outline" className="text-xs">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</Badge>
                            <span className="text-xs text-slate-400">{formatMinutes(totalMins)} est.</span>
                          </div>
                        </button>

                        {/* Task rows */}
                        {isOpen && (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="text-xs bg-white">
                                  <TableHead className="w-8 pl-3">
                                    <input type="checkbox" className="w-3.5 h-3.5 cursor-pointer"
                                      checked={tasks.every(t => selectedTaskIds.has(t.task_id)) && tasks.length > 0}
                                      onChange={() => {
                                        const allSelected = tasks.every(t => selectedTaskIds.has(t.task_id));
                                        setSelectedTaskIds(prev => {
                                          const next = new Set(prev);
                                          tasks.forEach(t => allSelected ? next.delete(t.task_id) : next.add(t.task_id));
                                          return next;
                                        });
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead className="w-16">Queue</TableHead>
                                  <TableHead className="whitespace-nowrap">Garment</TableHead>
                                  <TableHead>Customer</TableHead>
                                  <TableHead>Dispatch</TableHead>
                                  <TableHead>Priority</TableHead>
                                  <TableHead>Task</TableHead>
                                  <TableHead>Assigned To</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="whitespace-nowrap min-w-[150px]">Start</TableHead>
                                  <TableHead className="whitespace-nowrap min-w-[150px]">End</TableHead>
                                  <TableHead className="whitespace-nowrap">Time</TableHead>
                                  <TableHead className="min-w-[140px]">Notes</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tasks.map((task, taskIdx) => {
                                  const statusInfo = getProdStatusInfo(task.status);
                                  const isEditing = (field) => editingCell?.task_id === task.task_id && editingCell?.field === field;
                                  const startFmt = task.start_datetime ? new Date(task.start_datetime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                                  const endFmt = task.end_datetime ? new Date(task.end_datetime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                                  const blocked = isTaskBlocked(task);
                                  const isReordering = reorderingTaskId === task.task_id;

                                  return (
                                    <TableRow key={task.task_id} data-testid={`prod-task-row-${task.task_id}`}
                                      className={`text-sm ${selectedTaskIds.has(task.task_id) ? 'bg-blue-50' : blocked ? 'bg-amber-50/50' : ''}`}>
                                      {/* Checkbox */}
                                      <TableCell className="pl-3">
                                        <input type="checkbox" className="w-3.5 h-3.5 cursor-pointer"
                                          checked={selectedTaskIds.has(task.task_id)}
                                          onChange={() => toggleTaskSelection(task.task_id)}
                                          data-testid={`select-task-${task.task_id}`}
                                        />
                                      </TableCell>

                                      {/* Queue position + up/down */}
                                      <TableCell className="w-16">
                                        <div className="flex items-center gap-0.5">
                                          <span className="text-xs text-slate-400 w-5 text-right">{taskIdx + 1}</span>
                                          <div className="flex flex-col">
                                            <button
                                              className="hover:bg-slate-100 rounded p-0.5 disabled:opacity-30"
                                              onClick={() => handleMoveTask(task.task_id, 'up')}
                                              disabled={taskIdx === 0 || isReordering}
                                              data-testid={`move-up-${task.task_id}`}
                                            >
                                              {isReordering ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUp className="w-3 h-3 text-slate-500" />}
                                            </button>
                                            <button
                                              className="hover:bg-slate-100 rounded p-0.5 disabled:opacity-30"
                                              onClick={() => handleMoveTask(task.task_id, 'down')}
                                              disabled={taskIdx === tasks.length - 1 || isReordering}
                                              data-testid={`move-down-${task.task_id}`}
                                            >
                                              <ArrowDown className="w-3 h-3 text-slate-500" />
                                            </button>
                                          </div>
                                        </div>
                                      </TableCell>

                                      {/* Garment no + copy */}
                                      <TableCell className="font-mono text-xs font-medium whitespace-nowrap">
                                        <div className="flex items-center gap-1">
                                          <span>{task.garment_no || '—'}</span>
                                          {task.garment_no && (
                                            <button
                                              className="opacity-40 hover:opacity-100 transition-opacity"
                                              title="Copy to search"
                                              onClick={() => {
                                                setTaskSearch(task.garment_no);
                                                navigator.clipboard?.writeText(task.garment_no).catch(() => {});
                                              }}
                                              data-testid={`copy-garment-${task.task_id}`}
                                            >
                                              <Copy className="w-3 h-3 text-slate-500" />
                                            </button>
                                          )}
                                        </div>
                                      </TableCell>

                                      {/* Customer */}
                                      <TableCell className="text-xs max-w-[100px] truncate">{task.customer_name || '—'}</TableCell>

                                      {/* Dispatch date */}
                                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{task.due_date ? task.due_date.slice(0, 10) : '—'}</TableCell>

                                      {/* Priority */}
                                      <TableCell>
                                        {(() => {
                                          const priorityInfo = getPriorityConfig(task.priority || 'normal');
                                          return (
                                            <Badge className={`text-xs border ${priorityInfo.color}`} data-testid={`tailors-task-priority-${task.task_id}`}>
                                              {priorityInfo.label}
                                            </Badge>
                                          );
                                        })()}
                                      </TableCell>

                                      {/* Task + blocked badge */}
                                      <TableCell className="whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                          <Badge variant="outline" className="text-xs">{task.title?.split(' - ')[0] || task.task_key}</Badge>
                                          {blocked && (
                                            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 border" data-testid={`blocked-badge-${task.task_id}`}>
                                              Blocked
                                            </Badge>
                                          )}
                                        </div>
                                      </TableCell>

                                      {/* Reassign — inline dropdown */}
                                      <TableCell className="text-xs min-w-[130px]">
                                        {isEditing('assigned_tailor_id') ? (
                                          <Select value={task.assigned_tailor_id || '__none__'}
                                            onValueChange={val => {
                                              const t = tailors.find(t => t.tailor_id === val);
                                              saveCellUpdate(task.task_id, { assigned_tailor_id: t ? t.tailor_id : null, assigned_to: t ? [t.name] : [] });
                                            }}
                                            onOpenChange={open => { if (!open) setEditingCell(null); }}>
                                            <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {tailors.filter(t => t.is_active !== false).map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600 text-xs"
                                            onClick={() => startCellEdit(task.task_id, 'assigned_tailor_id', task.assigned_tailor_id)}
                                            data-testid={`reassign-cell-${task.task_id}`}>
                                            {(task.assigned_to || []).join(', ') || tailor?.name || <span className="text-slate-400 italic">Unassigned</span>}
                                          </span>
                                        )}
                                      </TableCell>

                                      {/* Status — inline dropdown */}
                                      <TableCell className="min-w-[120px]">
                                        {isEditing('status') ? (
                                          <Select value={task.status} onValueChange={val => saveCellUpdate(task.task_id, { status: val })} onOpenChange={open => { if (!open) setEditingCell(null); }}>
                                            <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {PROD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <Badge className={`text-xs border cursor-pointer transition-colors ${statusInfo.cls}`} onClick={() => startCellEdit(task.task_id, 'status', task.status)} data-testid={`prod-task-status-${task.task_id}`}>
                                            {statusInfo.label}
                                          </Badge>
                                        )}
                                      </TableCell>

                                      {/* Start datetime */}
                                      <TableCell className="text-xs min-w-[150px]">
                                        {isEditing('start_datetime') ? (
                                          <input type="datetime-local" className="text-xs border border-slate-300 rounded px-1 py-0.5 w-44 focus:outline-none focus:ring-1 focus:ring-slate-400"
                                            value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                            onBlur={() => handleCellBlur(task.task_id, 'start_datetime')} autoFocus />
                                        ) : (
                                          <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600"
                                            onClick={() => startCellEdit(task.task_id, 'start_datetime', task.start_datetime ? task.start_datetime.slice(0, 16) : '')}>
                                            {startFmt || <span className="text-slate-300 italic text-xs">Set start</span>}
                                          </span>
                                        )}
                                      </TableCell>

                                      {/* End datetime */}
                                      <TableCell className="text-xs min-w-[150px]">
                                        {isEditing('end_datetime') ? (
                                          <input type="datetime-local" className="text-xs border border-slate-300 rounded px-1 py-0.5 w-44 focus:outline-none focus:ring-1 focus:ring-slate-400"
                                            value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                            onBlur={() => handleCellBlur(task.task_id, 'end_datetime')} autoFocus />
                                        ) : (
                                          <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600"
                                            onClick={() => startCellEdit(task.task_id, 'end_datetime', task.end_datetime ? task.end_datetime.slice(0, 16) : '')}>
                                            {endFmt || <span className="text-slate-300 italic text-xs">Set end</span>}
                                          </span>
                                        )}
                                      </TableCell>

                                      {/* Time Taken */}
                                      <TableCell className="text-xs whitespace-nowrap">
                                        {isEditing('actual_minutes') ? (
                                          <input type="number" min="0" className="text-xs border border-amber-300 rounded px-1 py-0.5 w-20 bg-amber-50 focus:outline-none"
                                            placeholder="mins" value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                            onBlur={() => handleCellBlur(task.task_id, 'actual_minutes')} autoFocus />
                                        ) : (
                                          <span className="cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded"
                                            onClick={() => startCellEdit(task.task_id, 'actual_minutes', task.actual_minutes ?? '')}>
                                            {task.actual_minutes != null ? (
                                              <>{formatMinutes(task.actual_minutes)}{task.is_time_overridden && <span className="text-amber-500 ml-1">✎</span>}</>
                                            ) : task.estimated_minutes ? (
                                              <span className="text-slate-400">{formatMinutes(task.estimated_minutes)} est.</span>
                                            ) : <span className="text-slate-300 italic">Set</span>}
                                          </span>
                                        )}
                                      </TableCell>

                                      {/* Notes */}
                                      <TableCell className="text-xs min-w-[140px]">
                                        {isEditing('notes') ? (
                                          <input type="text" className="text-xs border border-slate-300 rounded px-1 py-0.5 w-36 focus:outline-none"
                                            value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                            onBlur={() => handleCellBlur(task.task_id, 'notes')}
                                            onKeyDown={e => e.key === 'Enter' && handleCellBlur(task.task_id, 'notes')} autoFocus />
                                        ) : (
                                          <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600 truncate block max-w-[140px]"
                                            onClick={() => startCellEdit(task.task_id, 'notes', task.notes || '')}>
                                            {task.notes || <span className="text-slate-300 italic text-xs">Add note</span>}
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Unassigned tasks */}
                  {tasksByTailor.unassigned.length > 0 && (
                    <div className="border border-red-200 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-red-50 hover:bg-red-100 transition-colors text-left"
                        onClick={() => setExpandedTailorGroups(prev => {
                          const next = new Set(prev);
                          next.has('__unassigned__') ? next.delete('__unassigned__') : next.add('__unassigned__');
                          return next;
                        })}
                      >
                        {expandedTailorGroups.has('__unassigned__') ? <ChevronDown className="w-4 h-4 text-red-400" /> : <ChevronRight className="w-4 h-4 text-red-400" />}
                        <span className="font-semibold text-sm text-red-700">Unassigned</span>
                        <Badge variant="outline" className="text-xs border-red-300 text-red-600 ml-auto">{tasksByTailor.unassigned.length} task{tasksByTailor.unassigned.length !== 1 ? 's' : ''}</Badge>
                      </button>
                      {expandedTailorGroups.has('__unassigned__') && (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="text-xs bg-white">
                                <TableHead className="w-8 pl-3"><input type="checkbox" className="w-3.5 h-3.5 cursor-pointer"
                                  checked={tasksByTailor.unassigned.every(t => selectedTaskIds.has(t.task_id)) && tasksByTailor.unassigned.length > 0}
                                  onChange={() => {
                                    const allSel = tasksByTailor.unassigned.every(t => selectedTaskIds.has(t.task_id));
                                    setSelectedTaskIds(prev => { const next = new Set(prev); tasksByTailor.unassigned.forEach(t => allSel ? next.delete(t.task_id) : next.add(t.task_id)); return next; });
                                  }} /></TableHead>
                                <TableHead className="whitespace-nowrap">Garment</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Priority</TableHead>
                                <TableHead>Task</TableHead>
                                <TableHead>Assign To</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Notes</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {tasksByTailor.unassigned.map(task => {
                                const statusInfo = getProdStatusInfo(task.status);
                                const isEditing = (field) => editingCell?.task_id === task.task_id && editingCell?.field === field;
                                return (
                                  <TableRow key={task.task_id} className={`text-sm ${selectedTaskIds.has(task.task_id) ? 'bg-blue-50' : ''}`}>
                                    <TableCell className="pl-3"><input type="checkbox" className="w-3.5 h-3.5 cursor-pointer" checked={selectedTaskIds.has(task.task_id)} onChange={() => toggleTaskSelection(task.task_id)} /></TableCell>
                                    <TableCell className="font-mono text-xs font-medium">
                                      <div className="flex items-center gap-1">
                                        <span>{task.garment_no || '—'}</span>
                                        {task.garment_no && (
                                          <button className="opacity-40 hover:opacity-100" title="Copy to search" onClick={() => { setTaskSearch(task.garment_no); navigator.clipboard?.writeText(task.garment_no).catch(() => {}); }}>
                                            <Copy className="w-3 h-3 text-slate-500" />
                                          </button>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-xs">{task.customer_name || '—'}</TableCell>
                                    <TableCell>
                                      {(() => {
                                        const priorityInfo = getPriorityConfig(task.priority || 'normal');
                                        return (
                                          <Badge className={`text-xs border ${priorityInfo.color}`} data-testid={`unassigned-task-priority-${task.task_id}`}>
                                            {priorityInfo.label}
                                          </Badge>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell><Badge variant="outline" className="text-xs">{task.title?.split(' - ')[0] || task.task_key}</Badge></TableCell>
                                    <TableCell className="min-w-[130px]">
                                      {isEditing('assigned_tailor_id') ? (
                                        <Select value={task.assigned_tailor_id || '__none__'}
                                          onValueChange={val => { const t = tailors.find(t => t.tailor_id === val); saveCellUpdate(task.task_id, { assigned_tailor_id: t ? t.tailor_id : null, assigned_to: t ? [t.name] : [] }); }}
                                          onOpenChange={open => { if (!open) setEditingCell(null); }}>
                                          <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="__none__"><span className="text-slate-400 italic">Unassigned</span></SelectItem>
                                            {tailors.filter(t => t.is_active !== false).map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded text-red-500 italic text-xs" onClick={() => startCellEdit(task.task_id, 'assigned_tailor_id', task.assigned_tailor_id)}>
                                          Click to assign
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={`text-xs border cursor-pointer transition-colors ${statusInfo.cls}`} onClick={() => startCellEdit(task.task_id, 'status', task.status)}>{statusInfo.label}</Badge>
                                    </TableCell>
                                    <TableCell className="text-xs min-w-[140px]">
                                      {isEditing('notes') ? (
                                        <input type="text" className="text-xs border rounded px-1 py-0.5 w-36" value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)} onBlur={() => handleCellBlur(task.task_id, 'notes')} onKeyDown={e => e.key === 'Enter' && handleCellBlur(task.task_id, 'notes')} autoFocus />
                                      ) : (
                                        <span className="cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded" onClick={() => startCellEdit(task.task_id, 'notes', task.notes || '')}>
                                          {task.notes || <span className="text-slate-300 italic">Add note</span>}
                                        </span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── All Tasks flat view ───────────────────────────────────── */}
              {workloadSubTab === 'all_tasks' && (
                filteredProductionTasks.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400" data-testid="all-tasks-empty">No tasks match the current filters.</div>
                ) : (
                  <div className="overflow-x-auto" data-testid="all-tasks-view">
                    <p className="text-xs text-slate-500 mb-2">{filteredProductionTasks.length} task{filteredProductionTasks.length !== 1 ? 's' : ''}</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="w-8">
                            <input type="checkbox"
                              className="w-3.5 h-3.5 cursor-pointer"
                              checked={filteredProductionTasks.length > 0 && filteredProductionTasks.every(t => selectedTaskIds.has(t.task_id))}
                              onChange={() => {
                                const allIds = filteredProductionTasks.map(t => t.task_id);
                                const allSelected = allIds.every(id => selectedTaskIds.has(id));
                                setSelectedTaskIds(prev => {
                                  const next = new Set(prev);
                                  allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                                  return next;
                                });
                              }}
                              data-testid="all-tasks-select-all"
                            />
                          </TableHead>
                          <TableHead className="whitespace-nowrap">Garment</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Dispatch</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead>Tailor</TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="whitespace-nowrap min-w-[150px]">Start</TableHead>
                          <TableHead className="whitespace-nowrap min-w-[150px]">End</TableHead>
                          <TableHead className="min-w-[130px]">Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProductionTasks.map(task => {
                          const tailor = tailors.find(t => t.tailor_id === task.assigned_tailor_id);
                          const statusInfo = PROD_STATUS_OPTIONS.find(s => s.value === task.status) || { label: task.status, cls: 'border-slate-300 text-slate-600' };
                          const blocked = isTaskBlocked(task);
                          const isEditing = (field) => editingCell?.task_id === task.task_id && editingCell?.field === field;
                          const startFmt = task.start_datetime ? new Date(task.start_datetime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                          const endFmt = task.end_datetime ? new Date(task.end_datetime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                          return (
                            <TableRow key={task.task_id} className={`text-sm ${selectedTaskIds.has(task.task_id) ? 'bg-blue-50' : ''}`} data-testid={`all-task-row-${task.task_id}`}>
                              <TableCell className="pl-3">
                                <input type="checkbox" className="w-3.5 h-3.5 cursor-pointer"
                                  checked={selectedTaskIds.has(task.task_id)}
                                  onChange={() => toggleTaskSelection(task.task_id)}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-xs font-medium whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <span>{task.garment_no || '—'}</span>
                                  {task.garment_no && (
                                    <button className="opacity-40 hover:opacity-100 transition-opacity" title="Copy"
                                      onClick={() => { setTaskSearch(task.garment_no); navigator.clipboard?.writeText(task.garment_no).catch(() => {}); }}
                                      data-testid={`all-copy-garment-${task.task_id}`}>
                                      <Copy className="w-3 h-3 text-slate-500" />
                                    </button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs max-w-[100px] truncate">{task.customer_name || '—'}</TableCell>
                              <TableCell className="text-xs text-slate-500 whitespace-nowrap">{task.due_date ? task.due_date.slice(0, 10) : '—'}</TableCell>
                              <TableCell>
                                {(() => {
                                  const priorityInfo = getPriorityConfig(task.priority || 'normal');
                                  return (
                                    <Badge className={`text-xs border ${priorityInfo.color}`} data-testid={`all-task-priority-${task.task_id}`}>
                                      {priorityInfo.label}
                                    </Badge>
                                  );
                                })()}
                              </TableCell>
                              {/* Tailor — editable */}
                              <TableCell className="text-xs min-w-[130px]">
                                {isEditing('assigned_tailor_id') ? (
                                  <Select value={task.assigned_tailor_id || '__none__'}
                                    onValueChange={val => { const t = tailors.find(t => t.tailor_id === val); saveCellUpdate(task.task_id, { assigned_tailor_id: t ? t.tailor_id : null, assigned_to: t ? [t.name] : [] }); }}
                                    onOpenChange={open => { if (!open) setEditingCell(null); }}>
                                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__"><span className="text-slate-400 italic">Unassigned</span></SelectItem>
                                      {tailors.filter(t => t.is_active !== false).map(t => <SelectItem key={t.tailor_id} value={t.tailor_id}>{t.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600 text-xs"
                                    onClick={() => startCellEdit(task.task_id, 'assigned_tailor_id', task.assigned_tailor_id)}
                                    data-testid={`all-reassign-${task.task_id}`}>
                                    {tailor?.name || (task.assigned_to || [])[0] || <span className="text-slate-400 italic">Unassigned</span>}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="outline" className="text-xs">{task.title?.split(' - ')[0] || task.task_key}</Badge>
                                  {blocked && <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 border">Blocked</Badge>}
                                </div>
                              </TableCell>
                              {/* Status — editable */}
                              <TableCell>
                                {isEditing('status') ? (
                                  <Select value={task.status} onValueChange={val => saveCellUpdate(task.task_id, { status: val })} onOpenChange={open => { if (!open) setEditingCell(null); }}>
                                    <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PROD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : (
                                  <Badge className={`text-xs border cursor-pointer transition-colors ${statusInfo.cls}`} onClick={() => startCellEdit(task.task_id, 'status', task.status)}>{statusInfo.label}</Badge>
                                )}
                              </TableCell>
                              {/* Start — editable */}
                              <TableCell className="text-xs min-w-[150px]">
                                {isEditing('start_datetime') ? (
                                  <input type="datetime-local" className="text-xs border border-slate-300 rounded px-1 py-0.5 w-44 focus:outline-none focus:ring-1 focus:ring-slate-400"
                                    value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                    onBlur={() => handleCellBlur(task.task_id, 'start_datetime')} autoFocus />
                                ) : (
                                  <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600"
                                    onClick={() => startCellEdit(task.task_id, 'start_datetime', task.start_datetime ? task.start_datetime.slice(0, 16) : '')}>
                                    {startFmt || <span className="text-slate-300 italic text-xs">Set start</span>}
                                  </span>
                                )}
                              </TableCell>
                              {/* End — editable */}
                              <TableCell className="text-xs min-w-[150px]">
                                {isEditing('end_datetime') ? (
                                  <input type="datetime-local" className="text-xs border border-slate-300 rounded px-1 py-0.5 w-44 focus:outline-none focus:ring-1 focus:ring-slate-400"
                                    value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                    onBlur={() => handleCellBlur(task.task_id, 'end_datetime')} autoFocus />
                                ) : (
                                  <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600"
                                    onClick={() => startCellEdit(task.task_id, 'end_datetime', task.end_datetime ? task.end_datetime.slice(0, 16) : '')}>
                                    {endFmt || <span className="text-slate-300 italic text-xs">Set end</span>}
                                  </span>
                                )}
                              </TableCell>
                              {/* Notes — editable */}
                              <TableCell className="text-xs min-w-[130px]">
                                {isEditing('notes') ? (
                                  <input type="text" className="text-xs border rounded px-1 py-0.5 w-32" value={editingCellValue} onChange={e => setEditingCellValue(e.target.value)}
                                    onBlur={() => handleCellBlur(task.task_id, 'notes')} onKeyDown={e => e.key === 'Enter' && handleCellBlur(task.task_id, 'notes')} autoFocus />
                                ) : (
                                  <span className="cursor-pointer px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-slate-600 truncate block max-w-[140px]"
                                    onClick={() => startCellEdit(task.task_id, 'notes', task.notes || '')}>
                                    {task.notes || <span className="text-slate-300 italic">Add note</span>}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )
              )}
              </>)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pattern_workload">
          <Card className="border-slate-200" data-testid="pattern-workload-tab">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Pattern Workload</CardTitle>
                <p className="text-xs text-slate-500">Grouped by client for pattern planning.</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchPatternWorkloads} data-testid="refresh-pattern-workload-button">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {workloadsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : patternWorkloads.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="pattern-workload-empty">
                  No pattern workload data available yet.
                </div>
              ) : (
                <Table data-testid="pattern-workload-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Garments</TableHead>
                      <TableHead>Total Pattern Minutes</TableHead>
                      <TableHead>Total Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patternWorkloads.map((entry, idx) => {
                      const minutes = Number(entry.total_minutes || 0);
                      const hours = (minutes / 60).toFixed(1);
                      const rowKey = entry.customer_id || entry.customer_name || idx;
                      return (
                        <TableRow key={rowKey} data-testid={`pattern-workload-row-${rowKey}`}>
                          <TableCell data-testid={`pattern-workload-client-${rowKey}`}>{entry.customer_name || 'Unknown Client'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              {(entry.garments || []).map(garment => (
                                <Badge key={garment.garment_id} variant="outline" data-testid={`pattern-workload-garment-${garment.garment_id}`}>
                                  {garment.garment_no} · {formatGarmentTypeLabel(garment.garment_type)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell data-testid={`pattern-workload-minutes-${rowKey}`}>{minutes} mins</TableCell>
                          <TableCell data-testid={`pattern-workload-hours-${rowKey}`}>{hours} hrs</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="task_configs">
          <Card className="border-slate-200" data-testid="task-configs-tab">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Garment Task Configs</CardTitle>
                <p className="text-xs text-slate-500">Define tasks and estimated minutes per garment type.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSeedTaskConfigs} data-testid="seed-task-configs-button">
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Seed Defaults
                </Button>
                <Button onClick={() => handleOpenTaskConfigDialog()} data-testid="add-task-config-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Config
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {taskConfigs.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="task-configs-empty">
                  No task configs yet. Seed defaults or add your first config.
                </div>
              ) : (
                <Table data-testid="task-configs-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Garment Type</TableHead>
                      <TableHead>Production Required</TableHead>
                      <TableHead>Tasks</TableHead>
                      <TableHead>Total Minutes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taskConfigs.map(config => (
                      <TableRow key={config.config_id} data-testid={`task-config-row-${config.config_id}`}>
                        <TableCell data-testid={`task-config-type-${config.config_id}`}>{formatGarmentTypeLabel(config.garment_type)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={config.production_required ? 'border-emerald-200 text-emerald-700' : 'border-amber-200 text-amber-700'} data-testid={`task-config-production-${config.config_id}`}>
                            {config.production_required ? 'Production' : 'Ordering Only'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {(config.tasks || []).length ? (
                              config.tasks.map(task => (
                                <Badge key={task.task_key} variant="outline" data-testid={`task-config-task-${config.config_id}-${task.task_key}`}>
                                  {task.label} · {task.task_category || 'General'} · {(task.planning_mode || 'auto').toUpperCase()} · {task.estimated_minutes || 0}m
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400">No tasks</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`task-config-minutes-${config.config_id}`}>{getTaskTotalMinutes(config.tasks)} mins</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenTaskConfigDialog(config)} data-testid={`edit-task-config-${config.config_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTaskConfig(config.config_id)} data-testid={`delete-task-config-${config.config_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </>
    )}


    {/* ── Assignment Planning Modal ──────────────────────────────────────────── */}
    <Dialog open={assignModalOpen} onOpenChange={(open) => { if (!open) { setAssignModalOpen(false); setPlanPreviewData(null); setDraftAssignments({}); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assign-modal">
        <DialogHeader>
          <DialogTitle>Plan & Assign Production Tasks</DialogTitle>
          <DialogDescription>
            Review recommendations based on current workload. Change any assignment as needed, then confirm.
          </DialogDescription>
        </DialogHeader>

        {planPreviewLoading ? (
          <div className="space-y-3 py-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : planPreviewData ? (() => {
          const liveLoad = computeLiveLoad(planPreviewData, draftAssignments);
          const shiftMap = planPreviewData.tailor_shift_minutes || {};
          const sortedPreviewGarments = [...(planPreviewData.garments || [])].sort((a, b) => {
            const prioDiff = getPriorityRank(b.priority) - getPriorityRank(a.priority);
            if (prioDiff !== 0) return prioDiff;
            return (a.garment_no || '').localeCompare(b.garment_no || '');
          });

          // Returns capacity in minutes for a tailor over the window up to dispatchDate
          const getWindowCap = (tailorId, dispatchDate) => {
            const shiftMins = shiftMap[tailorId] || 510;
            if (!dispatchDate) return shiftMins;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const dispatch = new Date(dispatchDate);
            const days = Math.max(1, Math.ceil((dispatch - today) / (1000 * 60 * 60 * 24)));
            return days * shiftMins;
          };

          const TailorBar = ({ tailorId, dispatchDate }) => {
            const load = liveLoad[tailorId] || 0;
            const cap = getWindowCap(tailorId, dispatchDate);
            const pct = Math.min((load / cap) * 100, 100);
            const isOver = load > cap;
            const barCls = isOver ? 'bg-red-400' : load > cap * 0.8 ? 'bg-amber-400' : 'bg-emerald-400';
            const shiftMins = shiftMap[tailorId] || 510;
            const days = dispatchDate
              ? Math.max(1, Math.ceil((new Date(dispatchDate) - Object.assign(new Date(), { hours: 0, minutes: 0, seconds: 0, ms: 0 })) / (1000 * 60 * 60 * 24)))
              : 1;
            return (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-300 ${barCls}`} style={{ width: `${pct}%` }} />
                </div>
                <span className={`text-xs w-28 text-right tabular-nums ${isOver ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                  {(load / 60).toFixed(1)}h / {(cap / 60).toFixed(1)}h ({days}d)
                </span>
              </div>
            );
          };

          return (
            <div className="space-y-5">
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="plan-preview-priority-policy">
                <p className="text-xs font-semibold text-blue-800">Scheduling policy</p>
                <p className="text-xs text-blue-700 mt-1">
                  {planPreviewData.planning_policy || 'Tasks are scheduled by sequence first (Cut before Sew), then garment priority (Urgent > High > Normal > Low).'}
                </p>
              </div>



              {planPreviewData.warnings?.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="plan-preview-warnings">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Planning warnings</p>
                  <ul className="space-y-1">
                    {planPreviewData.warnings.map((warning, index) => (
                      <li key={index} className="text-xs text-amber-700">• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {sortedPreviewGarments.map(g => {
                const priorityBadge = { urgent: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', normal: 'bg-slate-100 text-slate-600', low: 'bg-green-100 text-green-700' };
                return (
                  <div key={g.garment_id} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* Garment header */}
                    <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200">
                      <Badge className={`text-xs ${priorityBadge[g.priority] || priorityBadge.normal}`} data-testid={`assign-garment-priority-${g.garment_id}`}>{(g.priority || 'normal').toUpperCase()}</Badge>
                      <span className="font-mono font-semibold text-sm text-slate-800">{g.garment_no}</span>
                      <span className="text-xs text-slate-500">{formatGarmentTypeLabel(g.garment_type)}</span>
                      <span className="text-xs text-slate-500">·</span>
                      <span className="text-xs text-slate-500">{g.customer_name || '—'}</span>
                      <span className="ml-auto" />
                      {g.planned_dispatch_date && (
                        <span className="text-xs text-slate-500">Dispatch: {g.planned_dispatch_date}</span>
                      )}
                    </div>

                    {/* Tasks */}
                    <div className="divide-y divide-slate-100">
                      {g.tasks.length === 0 ? (
                        <p className="px-4 py-3 text-xs text-red-500 italic" data-testid={`assign-garment-issue-${g.garment_id}`}>
                          {g.assignment_issue || 'No matching product/task config exists for this garment type. Create a matching config before planning assignments.'}
                        </p>
                      ) : g.tasks.map(t => {
                        const key = `${g.garment_id}__${t.task_key}`;
                        const selectedTailor = draftAssignments[key];
                        const isRec = selectedTailor === t.recommended_tailor_id;
                        return (
                          <div key={t.task_key} className="px-4 py-3 flex flex-col gap-1.5">
                            <div className="flex items-center gap-3">
                              <div className="w-36 shrink-0">
                                <p className="text-sm font-medium text-slate-800">{t.label}</p>
                                <p className="text-xs text-slate-400">{Math.round(t.estimated_minutes / 60 * 10) / 10}h est.</p>
                              </div>
                              <div className="flex-1">
                                <Select
                                  value={selectedTailor || '__none__'}
                                  onValueChange={val => setDraftAssignments(prev => ({ ...prev, [key]: val === '__none__' ? undefined : val }))}
                                  data-testid={`assign-select-${g.garment_id}-${t.task_key}`}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Select tailor..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__"><span className="text-slate-400 italic">Unassigned</span></SelectItem>
                                    {t.eligible_tailors.map(et => (
                                      <SelectItem key={et.tailor_id} value={et.tailor_id}>
                                        <span className="flex items-center gap-2">
                                          {et.name}
                                          {et.tailor_id === t.recommended_tailor_id && (
                                            <span className="text-xs text-emerald-600 font-medium">(recommended)</span>
                                          )}
                                          <span className="text-xs text-slate-400">{Math.round((liveLoad[et.tailor_id] || 0) / 60 * 10) / 10}h loaded</span>
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {selectedTailor && selectedTailor !== '__none__' && (
                                  <TailorBar tailorId={selectedTailor} dispatchDate={g.planned_dispatch_date} />
                                )}
                                {t.eligible_tailors.length === 0 && (
                                  <p className="text-xs text-red-500 mt-1" data-testid={`assign-no-eligible-${g.garment_id}-${t.task_key}`}>
                                    {t.no_eligible_reason || 'No matching tailor exists for this garment type/task. Update tailor primary/secondary garment mapping or assign manually later.'}
                                  </p>
                                )}
                              </div>
                              {selectedTailor && selectedTailor !== '__none__' && (
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isRec ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
                                  {isRec ? '★' : '✎'}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <span className="font-medium">{Object.values(draftAssignments).filter(Boolean).length}</span> task{Object.values(draftAssignments).filter(Boolean).length !== 1 ? 's' : ''} ready to assign
                across <span className="font-medium">{sortedPreviewGarments.length}</span> garment{sortedPreviewGarments.length !== 1 ? 's' : ''}.
              </div>
            </div>
          );
        })() : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmAssignments}
            disabled={applyingAssignments || planPreviewLoading || Object.values(draftAssignments).filter(Boolean).length === 0}
            data-testid="confirm-assignments-button"
          >
            {applyingAssignments ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirm {Object.values(draftAssignments).filter(Boolean).length} Assignment{Object.values(draftAssignments).filter(Boolean).length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ── Add Ad-hoc Production Task Modal (Tailor Workload) ───────────────── */}
    <Dialog open={workloadAdHocOpen} onOpenChange={setWorkloadAdHocOpen}>
      <DialogContent className="max-w-2xl" data-testid="workload-ad-hoc-dialog">
        <DialogHeader>
          <DialogTitle>Add Ad-hoc Task</DialogTitle>
          <DialogDescription>
            Enter garment no + task, get recommendation, then override if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Garment No</Label>
              <Input
                value={workloadAdHocForm.garment_no}
                onChange={e => {
                  setWorkloadAdHocForm(prev => ({ ...prev, garment_no: e.target.value, task_key: '' }));
                  setWorkloadAdHocTaskOptions([]);
                  setWorkloadAdHocContext(null);
                  setWorkloadAdHocRecommendation(null);
                }}
                placeholder="e.g., SG33767J5-J1"
                className="h-9"
                data-testid="workload-ad-hoc-garment-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Load Task List</Label>
              <Button className="w-full" variant="outline" onClick={handleLoadWorkloadAdHocOptions} disabled={workloadAdHocOptionsLoading} data-testid="workload-ad-hoc-load-options-button">
                {workloadAdHocOptionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                Load
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Task (Config)</Label>
              <Select
                value={workloadAdHocForm.task_key || '__none__'}
                onValueChange={(value) => {
                  const nextKey = value === '__none__' ? '' : value;
                  const selectedTask = workloadAdHocTaskOptions.find(t => t.task_key === nextKey);
                  setWorkloadAdHocForm(prev => ({
                    ...prev,
                    task_key: nextKey,
                    estimated_minutes: selectedTask ? String(selectedTask.estimated_minutes ?? '') : prev.estimated_minutes,
                  }));
                }}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="workload-ad-hoc-task-select"><SelectValue placeholder="Select configured manual task" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select task</SelectItem>
                  {workloadAdHocTaskOptions.map(task => (
                    <SelectItem key={task.task_key} value={task.task_key}>
                      {task.label} ({task.task_category || 'General'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={workloadAdHocForm.status} onValueChange={v => setWorkloadAdHocForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="h-9 text-xs" data-testid="workload-ad-hoc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Estimated Minutes</Label>
              <Input type="number" min="0" value={workloadAdHocForm.estimated_minutes} onChange={e => setWorkloadAdHocForm(prev => ({ ...prev, estimated_minutes: e.target.value }))} className="h-9" data-testid="workload-ad-hoc-estimated-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Assign To</Label>
              <Select value={workloadAdHocForm.assigned_tailor_id || '__none__'} onValueChange={(v) => setWorkloadAdHocForm(prev => ({ ...prev, assigned_tailor_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger className="h-9 text-xs" data-testid="workload-ad-hoc-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {tailors.filter(t => t.is_active !== false).map(t => (
                    <SelectItem key={t.tailor_id} value={t.tailor_id}>
                      {t.name}{workloadAdHocRecommendation?.recommended_tailor_id === t.tailor_id ? ' (recommended)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Start (optional)</Label>
              <Input type="datetime-local" value={workloadAdHocForm.start_datetime} onChange={e => setWorkloadAdHocForm(prev => ({ ...prev, start_datetime: e.target.value }))} className="h-9" data-testid="workload-ad-hoc-start-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End (optional)</Label>
              <Input type="datetime-local" value={workloadAdHocForm.end_datetime} onChange={e => setWorkloadAdHocForm(prev => ({ ...prev, end_datetime: e.target.value }))} className="h-9" data-testid="workload-ad-hoc-end-input" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input value={workloadAdHocForm.notes} onChange={e => setWorkloadAdHocForm(prev => ({ ...prev, notes: e.target.value }))} className="h-9" placeholder="Optional notes" data-testid="workload-ad-hoc-notes-input" />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRecommendWorkloadAdHocTask} disabled={workloadAdHocLoading} data-testid="workload-ad-hoc-recommend-button">
              {workloadAdHocLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Recommend
            </Button>
            {workloadAdHocRecommendation?.recommendation_reason && (
              <span className="text-xs text-slate-500" data-testid="workload-ad-hoc-recommend-reason">{workloadAdHocRecommendation.recommendation_reason}</span>
            )}
          </div>

          {(workloadAdHocRecommendation?.garment || workloadAdHocContext) && (
            <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600" data-testid="workload-ad-hoc-garment-context">
              {(() => {
                const garment = workloadAdHocRecommendation?.garment || workloadAdHocContext;
                const selectedTask = workloadAdHocTaskOptions.find(t => t.task_key === workloadAdHocForm.task_key);
                return `${garment?.garment_no || '—'} · ${garment?.customer_name || '—'} · Priority: ${garment?.priority || 'normal'} · Dispatch: ${garment?.planned_dispatch_date || '—'}${selectedTask ? ` · Task: ${selectedTask.label} (${selectedTask.task_category || 'General'})` : ''}`;
              })()}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setWorkloadAdHocOpen(false)} data-testid="workload-ad-hoc-cancel-button">Cancel</Button>
          <Button onClick={handleCreateWorkloadAdHocTask} disabled={workloadAdHocSaving} data-testid="workload-ad-hoc-save-button">
            {workloadAdHocSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ── Tailor Bulk Edit Modal ──────────────────────────────────────────────── */}
    <Dialog open={tailorBulkEditOpen} onOpenChange={setTailorBulkEditOpen}>
      <DialogContent className="max-w-lg" data-testid="tailor-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Edit Tailors</DialogTitle>
          <DialogDescription>
            Editing <strong>{selectedTailorIds.size}</strong> tailor{selectedTailorIds.size > 1 ? 's' : ''}. Changes will be merged (Add) or subtracted (Remove) from existing values.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Field selector */}
          <div className="space-y-2">
            <Label>Field to edit</Label>
            <Select value={tailorBulkField} onValueChange={(v) => { setTailorBulkField(v); setTailorBulkValues([]); setTailorBulkAction('add'); }} data-testid="bulk-field-select">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primary_garment_types">Primary Garment Types</SelectItem>
                <SelectItem value="secondary_garment_types">Secondary Garment Types</SelectItem>
                <SelectItem value="capabilities">Capabilities</SelectItem>
                <SelectItem value="is_active">Active Status</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action selector — only for list fields */}
          {tailorBulkField !== 'is_active' && (
            <div className="space-y-2">
              <Label>Action</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkAction" value="add" checked={tailorBulkAction === 'add'} onChange={() => setTailorBulkAction('add')} data-testid="bulk-action-add" />
                  <span className="font-medium text-emerald-700">Add</span>
                  <span className="text-xs text-slate-400">(merge into existing)</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkAction" value="remove" checked={tailorBulkAction === 'remove'} onChange={() => setTailorBulkAction('remove')} data-testid="bulk-action-remove" />
                  <span className="font-medium text-red-600">Remove</span>
                  <span className="text-xs text-slate-400">(subtract from existing)</span>
                </label>
              </div>
            </div>
          )}

          {/* Value checkboxes */}
          {tailorBulkField === 'is_active' ? (
            <div className="space-y-2">
              <Label>Set status to</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkBool" value="true" checked={tailorBulkBoolValue === true} onChange={() => setTailorBulkBoolValue(true)} data-testid="bulk-bool-active" />
                  <span className="text-emerald-700 font-medium">Active</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkBool" value="false" checked={tailorBulkBoolValue === false} onChange={() => setTailorBulkBoolValue(false)} data-testid="bulk-bool-inactive" />
                  <span className="text-slate-600 font-medium">Inactive</span>
                </label>
              </div>
            </div>
          ) : (tailorBulkField === 'primary_garment_types' || tailorBulkField === 'secondary_garment_types') ? (
            <div className="space-y-2">
              <Label>Garment types</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {GARMENT_TYPE_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={tailorBulkValues.includes(opt.value)}
                      onCheckedChange={() => setTailorBulkValues(prev =>
                        prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]
                      )}
                      data-testid={`bulk-garment-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          ) : tailorBulkField === 'capabilities' ? (
            <div className="space-y-2">
              <Label>Capabilities</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {capabilityOptions.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={tailorBulkValues.includes(opt.value)}
                      onCheckedChange={() => setTailorBulkValues(prev =>
                        prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]
                      )}
                      data-testid={`bulk-capability-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {/* Selection summary */}
          {tailorBulkValues.length > 0 && (
            <p className="text-xs text-slate-500">
              Selected: <span className="font-medium text-slate-700">{(tailorBulkField === 'capabilities' ? tailorBulkValues.map(getCapabilityLabel) : tailorBulkValues.map(formatGarmentTypeLabel)).join(', ')}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setTailorBulkEditOpen(false)} data-testid="bulk-edit-cancel">Cancel</Button>
          <Button
            onClick={handleApplyTailorBulkEdit}
            disabled={tailorBulkSaving || (tailorBulkField !== 'is_active' && tailorBulkValues.length === 0)}
            data-testid="bulk-edit-apply"
          >
            {tailorBulkSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Apply to {selectedTailorIds.size} tailor{selectedTailorIds.size > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>


    <Dialog open={tailorDialogOpen} onOpenChange={(open) => { if (!open) resetTailorForm(); setTailorDialogOpen(open); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingTailor ? 'Edit Tailor' : 'Add Tailor'}</DialogTitle>
          <DialogDescription>Capture employee details and production capabilities.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={tailorForm.name}
                onChange={(e) => setTailorForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="tailor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Employee ID</Label>
              <Input
                value={tailorForm.employee_id}
                onChange={(e) => setTailorForm(prev => ({ ...prev, employee_id: e.target.value }))}
                data-testid="tailor-employee-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Team</Label>
              <Select
                value={tailorForm.team || '__none__'}
                onValueChange={(value) => setTailorForm(prev => ({ ...prev, team: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger data-testid="tailor-team-select">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {tailorTeamOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Primary Task</Label>
              <Select
                value={tailorForm.primary_task || '__none__'}
                onValueChange={(value) => setTailorForm(prev => ({ ...prev, primary_task: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger data-testid="tailor-primary-task-select">
                  <SelectValue placeholder="Select primary task" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {capabilityOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Capabilities</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {capabilityOptions.map(option => (
                <label key={option.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={tailorForm.capabilities.includes(option.value)}
                    onCheckedChange={() => toggleTailorCapability(option.value)}
                    data-testid={`tailor-capability-${option.value}`}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {/* Garment Types */}
          <div className="space-y-2">
            <Label>Primary Garments <span className="text-xs text-slate-400 font-normal">(garments they regularly work on)</span></Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {GARMENT_TYPE_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={(tailorForm.primary_garment_types || []).includes(opt.value)}
                    onCheckedChange={() => toggleGarmentType('primary_garment_types', opt.value)}
                    data-testid={`tailor-primary-garment-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Secondary Garments <span className="text-xs text-slate-400 font-normal">(emergency / overflow)</span></Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {GARMENT_TYPE_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={(tailorForm.secondary_garment_types || []).includes(opt.value)}
                    onCheckedChange={() => toggleGarmentType('secondary_garment_types', opt.value)}
                    data-testid={`tailor-secondary-garment-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Active</Label>
              <p className="text-xs text-slate-500">Inactive tailors will not receive auto-assignments.</p>
            </div>
            <Switch
              checked={tailorForm.is_active}
              onCheckedChange={(value) => setTailorForm(prev => ({ ...prev, is_active: value }))}
              data-testid="tailor-active-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setTailorDialogOpen(false)} data-testid="tailor-cancel-button">Cancel</Button>
          <Button onClick={handleSaveTailor} data-testid="tailor-save-button">
            {editingTailor ? 'Update Tailor' : 'Save Tailor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={taskConfigDialogOpen} onOpenChange={(open) => { if (!open) resetTaskConfigForm(); setTaskConfigDialogOpen(open); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTaskConfig ? 'Edit Task Config' : 'Add Task Config'}</DialogTitle>
          <DialogDescription>Define controlled task names, category, auto/manual planning mode, and capability requirement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Garment Type</Label>
              <Input
                list="garment-type-options"
                value={taskConfigForm.garment_type}
                onChange={(e) => setTaskConfigForm(prev => ({ ...prev, garment_type: e.target.value }))}
                data-testid="task-config-garment-type-input"
              />
              <datalist id="garment-type-options">
                {garmentTypeOptions.map(type => (
                  <option key={type} value={type} />
                ))}
              </datalist>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
              <div>
                <Label className="text-sm">Production Required</Label>
                <p className="text-xs text-slate-500">Toggle off for ordering-only items.</p>
              </div>
              <Switch
                checked={taskConfigForm.production_required}
                onCheckedChange={(value) => setTaskConfigForm(prev => ({ ...prev, production_required: value }))}
                data-testid="task-config-production-switch"
              />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Tasks</Label>
              <Button variant="outline" size="sm" onClick={handleAddTaskRow} data-testid="add-task-row-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </div>
            {taskConfigForm.tasks.length === 0 ? (
              <div className="text-sm text-slate-500" data-testid="task-config-no-tasks">
                No tasks added yet.
              </div>
            ) : (
              <div className="space-y-3">
                {taskConfigForm.tasks.map((task, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-7 gap-2 items-center" data-testid={`task-config-row-${index}`}>
                    <Select
                      value={task.task_category || 'Sewing'}
                      onValueChange={(value) => handleUpdateTaskRow(index, 'task_category', value)}
                    >
                      <SelectTrigger data-testid={`task-config-category-${index}`}>
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {taskCategoryOptions.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={task.task_key}
                      onValueChange={(value) => handleUpdateTaskRow(index, 'task_key', value)}
                    >
                      <SelectTrigger data-testid={`task-config-task-${index}`}>
                        <SelectValue placeholder="Task" />
                      </SelectTrigger>
                      <SelectContent>
                        {predefinedTaskLibrary
                          .filter(item => item.category === (task.task_category || 'Sewing'))
                          .map(item => (
                            <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    <div className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1.5" data-testid={`task-config-mode-${index}`}>
                      <Checkbox
                        checked={(task.planning_mode || 'auto') === 'manual'}
                        onCheckedChange={(checked) => handleUpdateTaskRow(index, 'planning_mode', checked ? 'manual' : 'auto')}
                        data-testid={`task-config-manual-checkbox-${index}`}
                      />
                      <span className="text-xs text-slate-600">Manual only</span>
                    </div>

                    <Input
                      type="number"
                      min="0"
                      placeholder="Minutes"
                      value={task.estimated_minutes}
                      onChange={(e) => handleUpdateTaskRow(index, 'estimated_minutes', e.target.value)}
                      data-testid={`task-config-minutes-${index}`}
                    />
                    <Input value={task.required_capability || ''} readOnly className="bg-slate-50 text-xs" data-testid={`task-config-capability-${index}`} />
                    <Input value={task.task_type || ''} readOnly className="bg-slate-50 text-xs" data-testid={`task-config-tasktype-${index}`} />
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveTaskRow(index)} data-testid={`task-config-remove-${index}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setTaskConfigDialogOpen(false)} data-testid="task-config-cancel-button">Cancel</Button>
          <Button onClick={handleTaskConfigSave} data-testid="task-config-save-button">
            {editingTaskConfig ? 'Update Config' : 'Save Config'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={autoAssignOpen} onOpenChange={setAutoAssignOpen}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Auto-Assign Preview</DialogTitle>
          <DialogDescription>Review changes before applying assignments.</DialogDescription>
        </DialogHeader>
        {autoAssignPreview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="border-slate-200">
                <CardContent className="py-3">
                  <p className="text-xs text-slate-500">Garments Scanned</p>
                  <p className="text-lg font-semibold" data-testid="auto-assign-garments-scanned">{autoAssignPreview.summary?.garments_scanned || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="py-3">
                  <p className="text-xs text-slate-500">Tasks Created</p>
                  <p className="text-lg font-semibold" data-testid="auto-assign-tasks-created">{autoAssignPreview.summary?.tasks_created || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="py-3">
                  <p className="text-xs text-slate-500">Assignments Updated</p>
                  <p className="text-lg font-semibold" data-testid="auto-assign-tasks-updated">{autoAssignPreview.summary?.tasks_assigned || 0}</p>
                </CardContent>
              </Card>
            </div>
            {autoAssignPreview.warnings?.length ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="auto-assign-warnings">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  Warnings
                </div>
                <ul className="mt-2 space-y-1 text-xs text-amber-700">
                  {autoAssignPreview.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {autoAssignPreview.changes?.length ? (
              <Table data-testid="auto-assign-changes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Garment</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Assignee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {autoAssignPreview.changes.map((change, index) => (
                    <TableRow key={change.task_id || index} data-testid={`auto-assign-change-${index}`}>
                      <TableCell>
                        <Badge variant="outline">{change.action}</Badge>
                      </TableCell>
                      <TableCell>{change.garment_no || '-'}</TableCell>
                      <TableCell>{change.task_label || change.task_key}</TableCell>
                      <TableCell>{(change.to_assigned_to || []).join(', ') || 'Unassigned'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-sm text-slate-500" data-testid="auto-assign-no-changes">
                No assignments or task creations were detected.
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500">No preview data yet.</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setAutoAssignOpen(false)} data-testid="auto-assign-cancel-button">
            Cancel
          </Button>
          <Button onClick={handleApplyAutoAssign} disabled={autoAssignLoading} data-testid="auto-assign-confirm-button">
            {autoAssignLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Apply Assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Tabs>
    </div>
    </div>
  );
};

export default GarmentsPage;
